/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/AiAssistantPanel.tsx
 * PURPOSE: Perplexity-style slide-out — Langflow streaming, markdown, stop, route-aware starters/follow-ups,
 *          tab-scoped thread history (per user + flow), selection→prefill bridge, regenerate, footer hints.
 * RECENT: 2026-07-15 [2.2.1590] — New/Clear chat clears sticky schedule continuation + Topic; drop stale confirm on Help me ask… pivot.
 * RECENT_PRIOR: 2026-07-15 [2.2.1583] — Prose-only create_contact confirms: data_result owns message (no duplicate bubble / Field Value).
 * RECENT_PRIOR: 2026-07-15 [2.2.1572] — Form-only CRUD turns (`form_request`) no longer render `(empty response)` bubble; FormCard carries copy.
 * RECENT_PRIOR: 2026-07-03 [2.2.1477] — **`/langflow-assistant`** proactive landing: composer stays pinned in **`assistant-page-composer-dock`**; hide duplicate footer/brain strip on proactive empty state; **`make dev`** unchanged.
 * RECENT_PRIOR: 2026-06-19 [2.2.1455] — **`MessagePreviewCard`** inline edit + save-as-template wiring; **`updateMessagePreview`** on stream hook; **`make dev`** unchanged.
 * RECENT_PRIOR: 2026-06-18 [2.2.1450] — Page landing footer restores quota banner + typing-preview **`assistant-stream-routing`**; **`AssistantPageLanding`** + rail **`AIAssistantRightRail`**; **`make dev`** unchanged.
 * RECENT_PRIOR: 2026-06-12 [2.2.1367] — Quota **429** wins over Langflow config help; user copy + Settings links when quota blocks stream or flow resolve.
 * RECENT: 2026-06-09 [2.2.1360] — Compact dock UI: flat chat scroll, auto-grow composer (128px cap), long-text wrap (`overflow-wrap: anywhere`), default dock height 560px; **`make dev`** unchanged.
 * RECENT_PRIOR: 2026-06-08 [2.2.1358] — Drop ref sync from resolved query type; microtask brain refresh after stream routing (CI dock resolve).
 * RECENT_PRIOR: 2026-06-08 [2.2.1357] — Context resolve refreshes after routed **`topic_slug`** commits (fixes dock **`team-messaging`** resolve race).
 * RECENT_PRIOR: 2026-06-08 [2.2.1350] — Chart **Ask about this** prefill keeps **`query_type: widget`** when **`assistantWidgetContext`** active; dock context-aware idle UX **[2.2.1351–1355]**.
 * RECENT_PRIOR: 2026-06-06 [2.2.1330] — **`AssistantEmailDraftCard`** (edit, copy, AI improve, save template); capability **`assistantWelcomeSubtitle`**; removed email/learned proactive welcome overlays.
 * RECENT_PRIOR: 2026-06-06 [2.2.1328] — proactive welcome email_templates mode; **`contextActionChipsHostedExternally`**; post-send coaching + brain refresh; scroll padding for welcome chips.
 * RECENT_PRIOR: 2026-05-26 [2.2.1271] — **`/langflow-assistant`** page variant: brain-in-graph tweaks, **`LangflowAssistantBrainStrip`**, preference saved toast, **`assistant-message-body`** / **`assistant-send-button`** test ids, **`useAssistantContext`** on page variant.
 * RECENT_PRIOR: 2026-05-11 Task 10 #86 — CRM **`embedPortalRoot`**:
 * RECENT_PRIOR: 2026-05-09 [2.2.901] **`safeRandomUUID`** on **`AiAssistantPanel`** message or assistant ids — avoids **`TypeError: crypto.randomUUID is not a function`** when **`randomUUID`** is absent (**`aiAssistantPersistence`**); root **`CHANGELOG` [2.2.901]`**; **`make dev`** unchanged.
 * RECENT_PRIOR: 2026-05-08 [2.2.893] **`embedPortalRoot`** + **`createPortal`** — CRM inset strip in entity drawer; **`elevatedStack`** z **`z-[140]`** / **`z-[145]`** when not embedded (above **`z-[128]`** contact shell) — root **`CHANGELOG` [2.2.893]`**
 * RECENT_PRIOR: [2.2.720] — Docs sync (**`CHANGELOG` [2.2.720]**); **`make dev`** unchanged.
 * RECENT_PRIOR: [2.2.718] — prefill subscriber: **`if (!pre) return`** (no spurious session clear); widget context before composer text so chart-only prefills update while panel stays open.
 * RECENT_PRIOR: [2.2.717] — restores active widget context on panel re-open from **`getActiveWidgetContext`**; clears session on × / Clear chat / New chat (**`setActiveWidgetContext`**).
 * RECENT_PRIOR: [2.2.716] — assistant prefill **`contextWidgetId`**; widget context banner above composer; **`data-testid`** **`assistant-composer-input`**, **`assistant-close`**, **`assistant-widget-context-banner`**. 2026-04-27 — [2.2.705] Panel **`data-testid`** (**`ai-panel-backdrop`**, **`ai-empty-state`**, **`ai-typing-indicator`**, **`ai-error-message`**); drawer closed drops **`role=\"dialog\"`** / **`aria-modal`**; Vitest **`AiAssistantPanel.test.tsx`** — root **`CHANGELOG` [2.2.705]**. [2.2.704] Markdown highlighting contract lives in **`AiAssistantMarkdown.tsx`** (**`rehype-highlight`** default grammars; see root **`CHANGELOG` [2.2.704]**); Playwright hljs regression uses wide viewport + **`reload`** (**`assistant.spec.ts`**). [2.2.703] Langflow proxy path + **`looksLikeLangflowUuidCandidate`** / docs **`PERPLEXITY_ASSISTANT_LANGFLOW.md`** — root **`CHANGELOG` [2.2.703]**. 2026-04-26 — **`flowResolving`** UX; platform health non-OK / non-JSON; **`usesPlatformLangflow`**; **`langflowAssistantFlow`** cache — root **`CHANGELOG` [2.2.702]**.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  PlusIcon,
  RectangleStackIcon,
  SparklesIcon,
  StopCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  createSessionId,
  createThreadId,
  safeRandomUUID,
  loadAssistantSession,
  loadHistoryIndex,
  loadThreadBody,
  persistThread,
  setActiveThreadInIndex,
  type ThreadHistoryEntry,
} from './aiAssistantPersistence';
import type { AiAssistantMessage } from './aiAssistantTypes';
import { useCurrentUser } from '@core/auth';
import { getApiBaseUrl, getAuthHeaders, getAuthToken } from '@shared/lib/frontend/api/config';
import { AiAssistantMarkdown } from './AiAssistantMarkdown';
import { AiAssistantSources } from './AiAssistantSources';
import { buildMarkdownTranscript, downloadMarkdownFile } from './aiAssistantExport';
import {
  extraMarkdownSourceUrls,
  partitionAssistantContent,
} from './assistantContentPartition';
import { applyAssistantHljsTheme } from './applyAssistantHljsTheme';
import { getAssistantPageHeadline } from './assistantPageContextLabel';
import { useDocRootIsDark } from './useDocRootIsDark';
import { runLangflowChat, type LangflowRunBody, type LangflowRunTransport } from './langflowRun';
import {
  ASSISTANT_UNIFIED_ORCHESTRATION_STREAM,
  resolveAgentIdForAssistantContext,
  mergeOrchestrationContinuationForSend,
  shouldDropStaleOrchestrationContinuation,
  streamOrchestrationAssistantText,
  getCrudCreateSubmitMessage,
  getFormCatalogSubmitMessage,
  type AssistantStreamRoutingMeta,
} from '../../../hooks/assistant';
import type { StreamSuggestedAction } from '../../../hooks/assistant/streamSuggestedActions';
import { AssistantFormRequestCard } from '../../../components/generative-ui/AssistantFormRequestCard';
import { DataTableCard } from '../../../components/generative-ui/DataTableCard';
import { EmailTemplateChoiceCard } from '../../../components/generative-ui/EmailTemplateChoiceCard';
import { AssistantEmailDraftCard } from '../../../components/generative-ui/AssistantEmailDraftCard';
import { MessagePreviewCard } from '../../../components/generative-ui/MessagePreviewCard';
import { parseAssistantEmailDraft } from './assistantEmailDraftUtils';
import { ApprovalModal } from '../../../components/assistant/ApprovalModal';
import { useEmailTemplateSavedCoaching } from '../../../hooks/assistant/useEmailTemplateSavedCoaching';
import { approveToolCall } from '../../../api/assistantApi';
import type { ApprovalModalBusy } from '../../../components/assistant/ApprovalModal';
import type { OrchestrationApprovalRequired } from '../../../hooks/assistant/streamOrchestrationAssistant';
import { fetchWorldModelContextMarkdown } from './fetchWorldModelContext';
import { buildAssistantThreadsWorldModelAppendix } from './assistantThreadsWorldModelAppendix';
import { getAssistantStarterPrompts } from './assistantContextualPrompts';
import { collectContextFollowUpChipItems } from '../../../components/assistant/assistantContextFollowUpChips';
import { resolveOperatorOutboundMessage } from '../../../hooks/assistant/assistantOperatorMode';
import { AssistantMessageFollowUpChips } from '../../../components/assistant/AssistantMessageFollowUpChips';
import { AssistantThinkingIndicator } from '../../../components/assistant/AssistantThinkingIndicator';
import {
  consumeAssistantPendingPrefill,
  getActiveWidgetContext,
  setActiveWidgetContext,
  subscribeAssistantOpenWithPrefill,
  type AssistantPrefillPayload,
} from './assistantPrefillBridge';
import {
  getAssistantLangflowFlowId,
  resetAssistantLangflowFlowIdCache,
} from '../../../services/langflowAssistantFlow';
import {
  isConcreteLangflowFlowId,
  looksLikeLangflowUuidCandidate,
} from '../../../services/langflowFlowId';
import { looksLikePreferenceLearningMessage } from './preferenceLearningHint';
import { useShellAssistantOptional } from '../../../layout/shellAssistant/ShellAssistantContext';
import { shellDrawerPanelStyle } from '../../../layout/shellDrawerLayout';
import { LangflowAssistantBrainStrip } from './LangflowAssistantBrainStrip';
import { useAssistantContext } from '../../../hooks/assistant';
import { inferAssistantTopicFromMessage } from '../../../hooks/assistant/inferAssistantTopicFromMessage';
import { focusEntityHintFromRoute } from '@shared/lib/frontend/utils/assistantRouteFocusHint';
import {
  buildDockFocusEntityHint,
  buildDockShellContext,
  collectDockIdleStarterChips,
  dealDisplayNameFromPageTitle,
  dockIdleComposerPlaceholder,
  inferDockIdleContextQueryType,
  resolveDockIdleChipPromptAtSend,
} from '../../../layout/shellAssistant/shellAssistantDockContext';
import { useSalesTargetSetOverlay } from '../../../hooks/useSalesTargetSetOverlay';
import { notifyBrainPreferencesChanged } from '@shared/lib/frontend/utils/brainPreferenceEvents';
import {
  requestOpenEmailTemplateCreate,
  subscribeAssistantBrainContextRefresh,
  triggerEmailTemplateLearningRefresh,
} from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import { requestOpenSalesTargetSet } from '@shared/lib/frontend/utils/salesTargetSetBridge';
import { resolveAssistantContextAction } from '@shared/lib/frontend/utils/assistantContextActionHandlers';
import type { AssistantContextAction } from '@shared/lib/frontend/api/assistantContextApi';
import { runBrowserLaunchFromAssistant } from '@platform/browser/frontend';
import { AssistantProactiveAdvisorWelcome } from '../../../components/assistant/AssistantProactiveAdvisorWelcome';
import {
  ASSISTANT_QUOTA_SETTINGS_PATH,
  assistantQuotaExceededBubbleMessage,
  clearAssistantQuotaExceeded,
  isAssistantQuotaExceededError,
  markAssistantQuotaExceeded,
  readAssistantQuotaExceeded,
} from '../../../components/assistant/assistantQuotaGuidance';
import {
  AssistantQuotaExceededBanner,
  AssistantQuotaSettingsLink,
} from '../../../components/assistant/AssistantQuotaExceededBanner';
import { hasProactiveAdvisorWelcome } from '../../../components/assistant/assistantProactiveAdvisorUtils';
import {
  assistantWelcomeSubtitle,
  collectAssistantWelcomeChips,
  type AssistantWelcomeChip,
} from '../../../components/assistant/assistantWelcomePrompts';
import { useAssistantQuotaBanner } from '../../../hooks/assistant/useAssistantQuotaBanner';
import { AssistantWelcomeChipButton } from '../../../components/assistant/AssistantWelcomeChipButton';
import { AssistantPageLanding } from './AssistantPageLanding';
import { AssistantPerplexityComposer } from './AssistantPerplexityComposer';
import { ASSISTANT_PAGE_BG_CLASS, ASSISTANT_RAIL_BG_CLASS } from './assistantPageTheme';
import {
  buildAssistantConnectorOutboundMessage,
  countActiveConnectors,
  resolvePrimaryLangflowFlowId,
} from './assistantConnectorOutbound';
import { useAssistantConnectors } from './useAssistantConnectors';

export type Message = AiAssistantMessage;

/** Langflow node ids — keep in sync with `scripts/seed/perplexity_assistant_brain_flow_lib.py`. */
const LANGFLOW_BRAIN_NODE = {
  worldModel: 'BraznWorldModel-shell',
  crmLookup: 'BraznCrmLookup-shell',
  searchDeals: 'BraznSearchDeals-shell',
} as const;

/** When true (default), brain context is resolved inside the Langflow graph, not injected as markdown. */
const langflowBrainInGraph = import.meta.env.VITE_LANGFLOW_BRAIN_IN_GRAPH !== 'false';

/** Optional route / document context sent to Langflow (`tweaks` + input prefix). */
export interface AiAssistantPageContext {
  path: string;
  title?: string;
}

export interface AiAssistantPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** `drawer` = right slide-over (default). `page` = embedded full-height column (no backdrop). */
  variant?: 'drawer' | 'page';
  /** Current page path/title — included in each run so the graph can ground answers. */
  pageContext?: AiAssistantPageContext | null;
  /** CRM embed: seed composer when `prefillNonce` bumps while open (or on open). */
  prefillContext?: string | null;
  /** Shell dock: send **`prefillContext`** on the next configured tick (chart/brain quick actions). */
  prefillAutoSend?: boolean;
  /** Increment when reopening with the same `prefillContext` text so the composer still refreshes. */
  prefillNonce?: number;
  /** CRM embed: quick-send chips rendered below the composer. */
  suggestedPrompts?: string[];
  /** When false, skip `subscribeAssistantOpenWithPrefill` (second panel instance, e.g. CRM shell). Default true. */
  subscribeGlobalPrefill?: boolean;
  /** CRM: stack above entity drawers and use a 400px drawer width. */
  elevatedStack?: boolean;
  /** CRM: portal drawer chrome into this node (e.g. bottom slot inside an entity drawer aside). */
  embedPortalRoot?: HTMLElement | null;
  /** ⌘K dock: brain rail already renders context action chips — hide welcome duplicates. */
  contextActionChipsHostedExternally?: boolean;
  /** Shell bottom dock: compact chrome; header moves into composer card. */
  dockEmbedded?: boolean;
  /** Shell right rail: Perplexity landing in a narrow column; transparent chrome. */
  railEmbedded?: boolean;
  /** Shell dock: advisor chips rendered directly above the composer input. */
  composerTopSlot?: ReactNode;
  /** Shell dock: show Automate in Browser control (CRM hand-off to remote Chromium). */
  browserAutomationEnabled?: boolean;
  /** Shell dock: live route meta for focus hints and shell_context [2.2.1345]. */
  shellRouteContext?: {
    route: string;
    selectedRecord?: string | null;
    activeFilters?: Record<string, unknown> | null;
  } | null;
}

function buildLangflowBody(
  trimmed: string,
  sid: string,
  ctx?: AiAssistantPageContext | null,
  worldModelMarkdown?: string | null,
  brainContext?: { tenantId?: string; userId?: string; threadId?: string } | null
): LangflowRunBody {
  const wm = (worldModelMarkdown ?? '').trim();
  const tweaks: Record<string, unknown> = {};
  const useBrainGraph = langflowBrainInGraph && Boolean(brainContext?.tenantId);

  if (ctx?.path?.trim()) {
    tweaks.brazn_page_context = JSON.stringify({
      path: ctx.path.trim(),
      title: (ctx.title ?? '').trim(),
    });
  }

  if (useBrainGraph && brainContext?.tenantId) {
    tweaks[LANGFLOW_BRAIN_NODE.worldModel] = {
      tenant_id: brainContext.tenantId,
      user_id: brainContext.userId ?? '',
      thread_id: brainContext.threadId ?? sid,
      user_message: trimmed,
    };
    const tenantTweak = {
      tenant_id: brainContext.tenantId,
      user_id: brainContext.userId ?? '',
      jwt: getAuthToken() ?? '',
    };
    tweaks[LANGFLOW_BRAIN_NODE.crmLookup] = tenantTweak;
    tweaks[LANGFLOW_BRAIN_NODE.searchDeals] = tenantTweak;
    // Langflow applies scalar tweak keys to every node that defines the field.
    tweaks.tenant_id = brainContext.tenantId;
    tweaks.user_id = brainContext.userId ?? '';
  } else if (wm) {
    tweaks.brazn_world_model_context = wm.length > 24_000 ? wm.slice(0, 24_000) : wm;
  }

  const blocks: string[] = [];
  if (ctx?.path?.trim()) {
    blocks.push(
      `[Current page]\nPath: ${ctx.path.trim()}${ctx.title?.trim() ? `\nTitle: ${ctx.title.trim()}` : ''}`
    );
  }
  if (!useBrainGraph && wm) {
    const maxPrefixWm = 12_000;
    const wmForPrefix =
      wm.length > maxPrefixWm ? `${wm.slice(0, maxPrefixWm)}\n\n…_(world model truncated here; full text in tweak **brazn_world_model_context**)_\n` : wm;
    blocks.push(`[World model / business context — Brazn tenant snapshot]\n${wmForPrefix}`);
  }
  const prefix = blocks.length > 0 ? `${blocks.join('\n\n---\n\n')}\n\n---\n\n` : '';

  return {
    input_value: prefix + trimmed,
    input_type: 'chat',
    output_type: 'chat',
    session_id: sid,
    tweaks,
  };
}

type LangflowConn =
  | null
  | { phase: 'checking' }
  | { phase: 'ok'; via: 'platform' | 'direct' }
  | { phase: 'down'; via: 'platform' | 'direct'; hint?: string };

/** Langflow SSE-derived status line for the in-flight assistant bubble. */
type StreamPhase = { assistantId: string; label: string } | null;

/** Distance from bottom above which we treat the user as "reading up" and stop auto-scrolling. */
const SCROLL_BOTTOM_THRESHOLD_PX = 80;

function formatShortThreadTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const LANGFLOW_CONFIG_HELP =
  'Either set VITE_LANGFLOW_FLOW_ID (repo root or shell .env.local), or keep it unset so the panel resolves the flow via GET /api/v1/platform/langflow/flows (gateway JWT) using the name in VITE_LANGFLOW_ASSISTANT_FLOW_NAME (default: Perplexity Assistant). For direct browser→Langflow, set VITE_LANGFLOW_API_URL and VITE_LANGFLOW_VIA_PLATFORM=false. See docs/assistant/PERPLEXITY_ASSISTANT_LANGFLOW.md and _shared/frontend-shell/.env.example.';

/** Perplexity-style caret while tokens are still arriving (paired with `loading` on the message). */
function StreamingCaret() {
  return (
    <span
      className="ml-0.5 inline-block h-[1.15em] w-2 animate-pulse rounded-sm bg-primary-500 align-text-bottom dark:bg-primary-400"
      aria-hidden
    />
  );
}

export function AiAssistantPanel({
  isOpen,
  onClose,
  variant = 'drawer',
  pageContext = null,
  prefillContext = null,
  prefillAutoSend = false,
  prefillNonce,
  suggestedPrompts = [],
  subscribeGlobalPrefill = true,
  elevatedStack = false,
  embedPortalRoot = null,
  contextActionChipsHostedExternally = false,
  dockEmbedded = false,
  railEmbedded = false,
  composerTopSlot = null,
  browserAutomationEnabled = false,
  shellRouteContext = null,
}: AiAssistantPanelProps) {
  const navigate = useNavigate();
  const isDocDark = useDocRootIsDark();
  const isDrawer = variant === 'drawer';
  const isEmbeddedDrawer = Boolean(embedPortalRoot) && isDrawer;
  const { user, isLoading: authLoading } = useCurrentUser();
  const shellAssistant = useShellAssistantOptional();
  const threadPersistenceScope = useMemo(() => {
    if (authLoading) return null;
    if (user?.id) return `${user.tenant_id}:${user.id}`;
    return 'anon';
  }, [authLoading, user?.id, user?.tenant_id]);

  const envFlowIdRaw = useMemo(
    () => (import.meta.env.VITE_LANGFLOW_FLOW_ID as string | undefined)?.trim() ?? '',
    []
  );
  const [resolvedFlowId, setResolvedFlowId] = useState<string | null>(null);
  const [flowResolveError, setFlowResolveError] = useState<string | null>(null);
  const prevTenantIdRef = useRef<string | undefined>(undefined);

  const viaPlatform = import.meta.env.VITE_LANGFLOW_VIA_PLATFORM === 'true';
  const baseUrl = (import.meta.env.VITE_LANGFLOW_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

  const unifiedOrchestration = ASSISTANT_UNIFIED_ORCHESTRATION_STREAM;
  const defaultAgentId = useMemo(
    () =>
      resolveAgentIdForAssistantContext(
        {
          path: pageContext?.path,
          title: pageContext?.title,
          prefillContext: prefillContext ?? undefined,
        },
        user?.tenant_id
      ),
    [pageContext?.path, pageContext?.title, prefillContext, user?.tenant_id]
  );

  const {
    catalog: connectorCatalog,
    selection: connectorSelection,
    loading: connectorsLoading,
    error: connectorsError,
    toggle: toggleConnector,
    isSelected: isConnectorSelected,
  } = useAssistantConnectors(threadPersistenceScope);

  const connectorSelectionRef = useRef(connectorSelection);
  const connectorCatalogRef = useRef(connectorCatalog);
  connectorSelectionRef.current = connectorSelection;
  connectorCatalogRef.current = connectorCatalog;

  const connectorActiveCount = countActiveConnectors(connectorSelection);

  const baseFlowId = isConcreteLangflowFlowId(envFlowIdRaw) ? envFlowIdRaw : (resolvedFlowId ?? '');
  const flowId = resolvePrimaryLangflowFlowId(connectorSelection, baseFlowId);
  const flowIdFromFlowsList = Boolean(resolvedFlowId) && !isConcreteLangflowFlowId(envFlowIdRaw);

  /** Thread persistence key: Langflow flow id, or synthetic key when using orchestration stream. */
  const persistenceFlowKey = useMemo(() => {
    if (unifiedOrchestration && defaultAgentId) {
      return `orch:${defaultAgentId}`;
    }
    return flowId;
  }, [unifiedOrchestration, defaultAgentId, flowId]);

  const persistenceReady = unifiedOrchestration
    ? Boolean(defaultAgentId)
    : isConcreteLangflowFlowId(flowId);

  const canPersistThreads =
    threadPersistenceScope !== null && persistenceReady;

  /** Runs and health checks use the gateway whenever env says so or the flow id came from the tenant flows list. */
  const usesPlatformLangflow = viaPlatform || flowIdFromFlowsList;

  const [activeThreadId, setActiveThreadId] = useState(() => createThreadId());
  const [sessionId, setSessionId] = useState(() => createSessionId());
  const [messages, setMessages] = useState<Message[]>([]);
  const [threadHistoryEntries, setThreadHistoryEntries] = useState<ThreadHistoryEntry[]>([]);
  const [threadHistoryOpen, setThreadHistoryOpen] = useState(false);
  const [input, setInput] = useState('');
  /** Dashboard chart suggestion: show “Asking about” banner until cleared or drawer closes. */
  const [assistantWidgetContext, setAssistantWidgetContext] = useState<{
    contextWidgetId: string;
    widgetTitle: string;
  } | null>(null);
  const salesTargetOverlay = useSalesTargetSetOverlay();
  const [isLoading, setIsLoading] = useState(false);
  const [langflowConn, setLangflowConn] = useState<LangflowConn>(null);
  /** When true, user scrolled up — do not auto-scroll on new tokens until they jump or send again. */
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [streamPhase, setStreamPhase] = useState<StreamPhase>(null);
  const [lastStreamRouting, setLastStreamRouting] = useState<AssistantStreamRoutingMeta | null>(
    null
  );
  /** Stream follow-up chips from complete.metadata (state ensures chip row re-renders) [2.2.1487]. */
  const [streamSuggestedActions, setStreamSuggestedActions] = useState<
    StreamSuggestedAction[] | undefined
  >(undefined);
  /** Pre-routing topic hint so brain context + chips update before SSE complete metadata arrives. */
  const [pendingTopicSlug, setPendingTopicSlug] = useState('');
  /** Stream-routed topic slug available synchronously for context/resolve [2.2.1357]. */
  const contextQueryTypeRef = useRef<string>('');
  const operatorPendingPromptRef = useRef<string | undefined>(undefined);
  /** Stream chips available before React commits lastStreamRouting (action-first complete) [2.2.1487]. */
  const streamSuggestedActionsRef = useRef<StreamSuggestedAction[] | undefined>(undefined);

  useEffect(() => {
    if (!unifiedOrchestration || !isOpen || isLoading) return;
    const draft = input.trim();
    if (!draft) {
      setPendingTopicSlug('');
      return;
    }
    const timer = window.setTimeout(() => {
      setPendingTopicSlug(inferAssistantTopicFromMessage(draft));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [input, unifiedOrchestration, isOpen, isLoading]);

  /** Keep shell dock brain rail in sync with pre-routing topic preview (AssistantDockBrainActions). */
  useEffect(() => {
    if (!unifiedOrchestration || !shellAssistant) return;
    const slug = pendingTopicSlug.trim();
    const draft = input.trim();
    if (draft && slug && !isLoading) {
      shellAssistant.setOrchestrationRouting({ topicSlug: slug });
      return;
    }
    if (!draft && !isLoading) {
      const lastSlug = lastStreamRouting?.topicSlug?.trim();
      shellAssistant.setOrchestrationRouting(
        lastSlug
          ? {
              topicSlug: lastSlug,
              topicMatchConfidence: lastStreamRouting?.topicMatchConfidence,
            }
          : null
      );
    }
  }, [
    pendingTopicSlug,
    input,
    unifiedOrchestration,
    shellAssistant,
    isLoading,
    lastStreamRouting,
  ]);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingAutoSendRef = useRef<string | null>(null);
  const sendMessageWithTextRef = useRef<(raw: string) => void>(() => {});
  const abortRef = useRef<AbortController | null>(null);
  /** Gmail / messaging tool_continue from followup_question on unified orchestration stream. */
  const pendingOrchestrationContinuationRef = useRef<Record<string, unknown> | null>(null);
  const threadHistoryMenuRef = useRef<HTMLDivElement>(null);
  const threadHistoryButtonRef = useRef<HTMLButtonElement>(null);
  const persistSnapshotRef = useRef({ activeThreadId, sessionId, messages });
  persistSnapshotRef.current = { activeThreadId, sessionId, messages };
  /** `${threadPersistenceScope}:${flowId}` last applied in the hydration layout effect. */
  const threadHydrationKeyRef = useRef('');

  const apiKey = (import.meta.env.VITE_LANGFLOW_API_KEY as string | undefined)?.trim();
  const useStream = import.meta.env.VITE_LANGFLOW_STREAM !== 'false';

  const transport: LangflowRunTransport = useMemo(() => {
    if (viaPlatform || flowIdFromFlowsList) {
      return { kind: 'platform' as const };
    }
    return { kind: 'direct' as const, baseUrl, ...(apiKey ? { apiKey } : {}) };
  }, [apiKey, baseUrl, flowIdFromFlowsList, viaPlatform]);

  const configured = unifiedOrchestration
    ? Boolean(
        threadPersistenceScope &&
          threadPersistenceScope !== 'anon' &&
          persistenceReady
      )
    : isConcreteLangflowFlowId(flowId) &&
      (viaPlatform || Boolean(baseUrl.trim()) || flowIdFromFlowsList);

  /** Flow id from `/platform/langflow/flows` is still in flight (no env UUID yet, no error). */
  const flowResolving = useMemo(
    () =>
      !unifiedOrchestration &&
      isOpen &&
      !authLoading &&
      threadPersistenceScope !== null &&
      threadPersistenceScope !== 'anon' &&
      !isConcreteLangflowFlowId(envFlowIdRaw) &&
      !isConcreteLangflowFlowId(flowId) &&
      flowResolveError === null,
    [
      authLoading,
      envFlowIdRaw,
      persistenceFlowKey,
      flowResolveError,
      isOpen,
      threadPersistenceScope,
    ]
  );

  /** Clear cached Langflow flow id when the signed-in tenant changes (flows list is tenant-scoped via JWT). */
  useEffect(() => {
    const tid = user?.tenant_id;
    if (!tid) return;
    if (prevTenantIdRef.current !== undefined && prevTenantIdRef.current !== tid) {
      resetAssistantLangflowFlowIdCache();
      setResolvedFlowId(null);
      setFlowResolveError(null);
    }
    prevTenantIdRef.current = tid;
  }, [user?.tenant_id]);

  /**
   * When `VITE_LANGFLOW_FLOW_ID` is unset or a placeholder, resolve once per JS module load via
   * **`GET /api/v1/platform/langflow/flows`** (subsequent panel opens reuse the cached promise).
   */
  useEffect(() => {
    if (unifiedOrchestration) return;
    if (!isOpen || authLoading) return;
    if (threadPersistenceScope === null || threadPersistenceScope === 'anon') return;
    if (isConcreteLangflowFlowId(envFlowIdRaw)) {
      setResolvedFlowId(null);
      setFlowResolveError(null);
      return;
    }

    let cancelled = false;
    setFlowResolveError(null);
    void getAssistantLangflowFlowId()
      .then((id) => {
        if (!cancelled) {
          setResolvedFlowId(id);
          setFlowResolveError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setResolvedFlowId(null);
          const msg = err instanceof Error ? err.message : String(err);
          setFlowResolveError(msg);
          if (isAssistantQuotaExceededError(msg)) {
            markAssistantQuotaExceeded();
            setSessionQuotaExceeded(true);
          }
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, authLoading, threadPersistenceScope, envFlowIdRaw, unifiedOrchestration]);

  const pageContextRef = useRef(pageContext);
  useEffect(() => {
    pageContextRef.current = pageContext ?? null;
  }, [pageContext]);

  /** Cached world-model markdown (same source as main assistant / world view). */
  const worldModelMarkdownRef = useRef('');
  const [worldModelContextLoaded, setWorldModelContextLoaded] = useState(false);
  const [preferenceSavedHint, setPreferenceSavedHint] = useState(false);
  const [sessionQuotaExceeded, setSessionQuotaExceeded] = useState(() => readAssistantQuotaExceeded());
  const quotaBannerState = useAssistantQuotaBanner(isOpen, sessionQuotaExceeded, () =>
    setSessionQuotaExceeded(false)
  );
  const quotaBlocked = quotaBannerState.isExceeded;
  const [pendingApproval, setPendingApproval] = useState<OrchestrationApprovalRequired | null>(
    null
  );
  const [approvalBusy, setApprovalBusy] = useState<ApprovalModalBusy>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !configured || langflowBrainInGraph) {
      worldModelMarkdownRef.current = '';
      setWorldModelContextLoaded(false);
      return;
    }
    const ac = new AbortController();
    setWorldModelContextLoaded(false);
    void (async () => {
      const md = await fetchWorldModelContextMarkdown(ac.signal);
      if (!ac.signal.aborted) {
        worldModelMarkdownRef.current = md;
        setWorldModelContextLoaded(Boolean(md));
      }
    })();
    return () => ac.abort();
  }, [isOpen, configured]);

  /** Swap highlight.js stylesheet when user toggles light/dark (markdown code blocks). */
  useLayoutEffect(() => {
    applyAssistantHljsTheme(isDocDark);
  }, [isDocDark]);

  /** Load or reset persisted thread when auth scope or Langflow flow id changes (sessionStorage, this tab). */
  useLayoutEffect(() => {
    abortRef.current?.abort();
    if (threadPersistenceScope === null) return;

    if (!persistenceReady) {
      // Flow id still resolving via gateway — keep session/thread stable until concrete.
      setIsLoading(false);
      setStreamPhase(null);
      setAwayFromBottom(false);
      setEditingUserId(null);
      setEditDraft('');
      return;
    }

    const hydrationKey = `${threadPersistenceScope}:${persistenceFlowKey}`;
    const session = loadAssistantSession(threadPersistenceScope, persistenceFlowKey);
    setActiveThreadId(session.activeThreadId);
    setSessionId((prev) => {
      if (session.messages.length > 0) {
        threadHydrationKeyRef.current = hydrationKey;
        return session.sessionId;
      }
      const prevKey = threadHydrationKeyRef.current;
      threadHydrationKeyRef.current = hydrationKey;
      if (prevKey === hydrationKey) return prev;
      if (prevKey && prevKey !== hydrationKey) return session.sessionId;
      return prev || session.sessionId;
    });
    setMessages(session.messages);
    setThreadHistoryEntries(session.historyEntries);
    setThreadHistoryOpen(false);
    setIsLoading(false);
    setStreamPhase(null);
    setAwayFromBottom(false);
    setEditingUserId(null);
    setEditDraft('');
  }, [threadPersistenceScope, persistenceFlowKey, persistenceReady]);

  useEffect(() => {
    if (!isOpen || !configured) {
      setLangflowConn(null);
      return;
    }
    if (unifiedOrchestration) {
      setLangflowConn({ phase: 'ok', via: 'platform' });
      return;
    }
    const ac = new AbortController();
    setLangflowConn({ phase: 'checking' });

    const probe = async () => {
      try {
        if (usesPlatformLangflow) {
          const url = `${getApiBaseUrl().replace(/\/$/, '')}/api/v1/platform/langflow/health`;
          const r = await fetch(url, { headers: getAuthHeaders(), signal: ac.signal });
          if (ac.signal.aborted) return;
          const raw = await r.text();
          let j: { reachable?: boolean; error?: string; detail?: string } = {};
          try {
            j = raw ? (JSON.parse(raw) as typeof j) : {};
          } catch {
            setLangflowConn({
              phase: 'down',
              via: 'platform',
              hint: r.ok ? 'Invalid health response' : raw.slice(0, 200) || r.statusText || `HTTP ${r.status}`,
            });
            return;
          }
          if (!r.ok) {
            const hint =
              typeof j.detail === 'string'
                ? j.detail
                : typeof j.error === 'string'
                  ? j.error
                  : raw.slice(0, 200) || r.statusText || `HTTP ${r.status}`;
            setLangflowConn({ phase: 'down', via: 'platform', hint });
            return;
          }
          if (j.reachable === true) {
            setLangflowConn({ phase: 'ok', via: 'platform' });
          } else {
            setLangflowConn({
              phase: 'down',
              via: 'platform',
              hint: typeof j.error === 'string' ? j.error : undefined,
            });
          }
          return;
        }

        const base = baseUrl.replace(/\/$/, '');
        let reachedServer = false;
        for (const path of ['/health', '/api/v1/health']) {
          try {
            const r = await fetch(`${base}${path}`, { method: 'GET', signal: ac.signal });
            if (ac.signal.aborted) return;
            reachedServer = true;
            if (r.ok || (r.status < 500 && r.status !== 404)) {
              setLangflowConn({ phase: 'ok', via: 'direct' });
              return;
            }
          } catch {
            /* CORS or network — try next path */
          }
        }
        if (!ac.signal.aborted) {
          setLangflowConn(
            reachedServer ? { phase: 'down', via: 'direct' } : null
          );
        }
      } catch {
        if (ac.signal.aborted) return;
        setLangflowConn(usesPlatformLangflow ? { phase: 'down', via: 'platform' } : null);
      }
    };

    void probe();
    return () => ac.abort();
  }, [isOpen, configured, usesPlatformLangflow, baseUrl, unifiedOrchestration]);

  const onScrollMessages = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const away = dist > SCROLL_BOTTOM_THRESHOLD_PX;
    setAwayFromBottom((prev) => (prev === away ? prev : away));
  }, []);

  const jumpToLatest = useCallback(() => {
    setAwayFromBottom(false);
    window.requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (awayFromBottom) return;
    if (scrollRafRef.current != null) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: isLoading ? 'auto' : 'smooth' });
    });
    return () => {
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [messages, isOpen, awayFromBottom, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  /** CRM (and other embeds): controlled prefill via props + nonce — avoids global document bridge collisions. */
  useEffect(() => {
    if (prefillNonce === undefined || prefillNonce === null) return;
    if (!isOpen) return;
    const t = (prefillContext ?? '').trim();
    if (!t) return;
    if (prefillAutoSend) {
      pendingAutoSendRef.current = t;
      return;
    }
    setInput(t);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [isOpen, prefillNonce, prefillContext, prefillAutoSend]);

  /** Auto-grow composer (Perplexity-style), capped so the panel keeps room for messages. */
  const composerMaxHeightPx = dockEmbedded ? 128 : 200;

  const syncComposerHeight = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = '0px';
    const next = Math.min(ta.scrollHeight, composerMaxHeightPx);
    ta.style.height = `${next}px`;
    ta.style.overflowY = ta.scrollHeight > composerMaxHeightPx ? 'auto' : 'hidden';
  }, [composerMaxHeightPx]);

  useEffect(() => {
    syncComposerHeight();
  }, [input, isOpen, syncComposerHeight]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (threadHistoryOpen) {
        e.preventDefault();
        setThreadHistoryOpen(false);
        return;
      }
      if (editingUserId) {
        e.preventDefault();
        setEditingUserId(null);
        setEditDraft('');
        return;
      }
      // CRM entity drawer: Langflow strip is portaled into `embedPortalRoot`. When focus lives in the
      // drawer body (not the strip), do not call `onClose` here — the drawer FocusTrap consumes Escape
      // and runs a single dismiss path (see OpportunityDrawer `flushClose`).
      if (isEmbeddedDrawer && embedPortalRoot) {
        const ae = document.activeElement;
        if (ae && !embedPortalRoot.contains(ae)) {
          return;
        }
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingUserId, embedPortalRoot, isEmbeddedDrawer, isOpen, onClose, threadHistoryOpen]);

  useEffect(() => {
    if (!threadHistoryOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (threadHistoryMenuRef.current?.contains(t)) return;
      if (threadHistoryButtonRef.current?.contains(t)) return;
      setThreadHistoryOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [threadHistoryOpen]);

  useEffect(() => {
    if (!isOpen || !isDrawer) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isDrawer, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setAwayFromBottom(false);
      setEditingUserId(null);
      setEditDraft('');
      setThreadHistoryOpen(false);
    }
  }, [isOpen]);

  /** Rehydrate chart banner after minimize or remount (session module store; not localStorage). */
  useEffect(() => {
    if (!isOpen) return;
    setAssistantWidgetContext((prev) => {
      if (prev) return prev;
      const ctx = getActiveWidgetContext();
      if (!ctx) return prev;
      const wid = ctx.contextWidgetId?.trim();
      if (!wid) return prev;
      return { contextWidgetId: wid, widgetTitle: (ctx.widgetTitle ?? '').trim() || 'Chart' };
    });
  }, [isOpen]);

  const stopGeneration = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const flushPersistCurrentThread = useCallback(() => {
    if (!canPersistThreads) return;
    if (!messages.some((m) => !m.loading)) return;
    const result = persistThread(
      threadPersistenceScope,
      persistenceFlowKey,
      activeThreadId,
      sessionId,
      messages
    );
    setThreadHistoryEntries(result.historyEntries);
    if (result.activeThreadId !== activeThreadId) {
      setActiveThreadId(result.activeThreadId);
      const body = loadThreadBody(threadPersistenceScope, persistenceFlowKey, result.activeThreadId);
      setSessionId(body?.sessionId ?? createSessionId());
      setMessages(body?.messages ?? []);
    }
  }, [activeThreadId, canPersistThreads, messages, persistenceFlowKey, sessionId, threadPersistenceScope]);

  const clearChat = useCallback(() => {
    stopGeneration();
    if (canPersistThreads) {
      const result = persistThread(
        threadPersistenceScope,
        persistenceFlowKey,
        activeThreadId,
        sessionId,
        []
      );
      setActiveThreadId(result.activeThreadId);
      setThreadHistoryEntries(result.historyEntries);
      const body = loadThreadBody(threadPersistenceScope, persistenceFlowKey, result.activeThreadId);
      setSessionId(body?.sessionId ?? createSessionId());
      setMessages(body?.messages ?? []);
    } else {
      setSessionId(createSessionId());
      setMessages([]);
    }
    setInput('');
    setAwayFromBottom(false);
    setEditingUserId(null);
    setEditDraft('');
    setStreamPhase(null);
    setAssistantWidgetContext(null);
    setActiveWidgetContext(null);
    // [2.2.1590] Clear chat must not reuse prior schedule_meeting confirmation.
    pendingOrchestrationContinuationRef.current = null;
    setLastStreamRouting(null);
    setStreamSuggestedActions(undefined);
    streamSuggestedActionsRef.current = undefined;
    contextQueryTypeRef.current = '';
    setPendingTopicSlug('');
    operatorPendingPromptRef.current = undefined;
    shellAssistant?.setOrchestrationRouting(null);
  }, [
    activeThreadId,
    canPersistThreads,
    persistenceFlowKey,
    sessionId,
    shellAssistant,
    stopGeneration,
    threadPersistenceScope,
  ]);

  const openNewAssistantThread = useCallback(() => {
    if (!canPersistThreads) return;
    stopGeneration();
    flushPersistCurrentThread();
    const tid = createThreadId();
    const idx = setActiveThreadInIndex(threadPersistenceScope, persistenceFlowKey, tid);
    setActiveThreadId(tid);
    setThreadHistoryEntries(idx.entries);
    setSessionId(createSessionId());
    setMessages([]);
    setInput('');
    setAwayFromBottom(false);
    setEditingUserId(null);
    setEditDraft('');
    setStreamPhase(null);
    setThreadHistoryOpen(false);
    setAssistantWidgetContext(null);
    setActiveWidgetContext(null);
    // [2.2.1590] Fresh thread — drop sticky invite confirm + Topic chip.
    pendingOrchestrationContinuationRef.current = null;
    setLastStreamRouting(null);
    setStreamSuggestedActions(undefined);
    streamSuggestedActionsRef.current = undefined;
    contextQueryTypeRef.current = '';
    setPendingTopicSlug('');
    operatorPendingPromptRef.current = undefined;
    shellAssistant?.setOrchestrationRouting(null);
  }, [
    flushPersistCurrentThread,
    persistenceFlowKey,
    shellAssistant,
    stopGeneration,
    threadPersistenceScope,
  ]);

  const switchToHistoryThread = useCallback(
    (threadId: string) => {
      if (!canPersistThreads || threadId === activeThreadId) {
        setThreadHistoryOpen(false);
        return;
      }
      stopGeneration();
      flushPersistCurrentThread();
      setActiveThreadInIndex(threadPersistenceScope, persistenceFlowKey, threadId);
      const body = loadThreadBody(threadPersistenceScope, persistenceFlowKey, threadId);
      const idx = loadHistoryIndex(threadPersistenceScope, persistenceFlowKey);
      setActiveThreadId(threadId);
      setThreadHistoryEntries(idx?.entries ?? []);
      setSessionId(body?.sessionId ?? createSessionId());
      setMessages(body?.messages ?? []);
      setInput('');
      setAwayFromBottom(false);
      setEditingUserId(null);
      setEditDraft('');
      setStreamPhase(null);
      setThreadHistoryOpen(false);
    },
    [
      activeThreadId,
      canPersistThreads,
      persistenceFlowKey,
      flushPersistCurrentThread,
      stopGeneration,
      threadPersistenceScope,
    ]
  );

  useEffect(() => {
    if (!canPersistThreads) return;
    const t = window.setTimeout(() => {
      const snap = persistSnapshotRef.current;
      if (!snap.messages.some((m) => !m.loading)) return;
      const result = persistThread(
        threadPersistenceScope,
        persistenceFlowKey,
        snap.activeThreadId,
        snap.sessionId,
        snap.messages
      );
      setThreadHistoryEntries(result.historyEntries);
      if (result.activeThreadId !== snap.activeThreadId) {
        setActiveThreadId(result.activeThreadId);
        const body = loadThreadBody(threadPersistenceScope, persistenceFlowKey, result.activeThreadId);
        setSessionId(body?.sessionId ?? createSessionId());
        setMessages(body?.messages ?? []);
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [activeThreadId, canPersistThreads, messages, persistenceFlowKey, threadPersistenceScope]);

  useEffect(() => {
    if (!preferenceSavedHint) return;
    const t = window.setTimeout(() => setPreferenceSavedHint(false), 6000);
    return () => window.clearTimeout(t);
  }, [preferenceSavedHint]);

  const lastUserMessageText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') return messages[i].content.trim();
    }
    return '';
  }, [messages]);

  const langflowPageContextMessage = input.trim() || lastUserMessageText;
  const langflowBrainContextEnabled =
    isOpen && !authLoading && Boolean(user?.tenant_id);
  /** Bottom dock and right rail both send live shell_context on context/resolve [2.2.1450]. */
  const shellContextEmbedded = dockEmbedded || railEmbedded;
  const langflowContextQueryType =
    lastStreamRouting?.topicSlug?.trim() ||
    pendingTopicSlug.trim() ||
    'general_chat';
  const dockShellRouteMeta = useMemo(
    () =>
      shellRouteContext
        ? {
            route: shellRouteContext.route,
            selectedRecord: shellRouteContext.selectedRecord ?? null,
            activeFilters: shellRouteContext.activeFilters ?? null,
          }
        : {
            route: pageContext?.path ?? '',
            selectedRecord: null,
            activeFilters: null,
          },
    [shellRouteContext, pageContext?.path]
  );

  const langflowFocusEntityHint = useMemo(() => {
    if (shellContextEmbedded) {
      return buildDockFocusEntityHint(dockShellRouteMeta);
    }
    return focusEntityHintFromRoute(pageContext?.path ?? '');
  }, [shellContextEmbedded, dockShellRouteMeta, pageContext?.path]);

  // Live ref so the (ref-based) stream handler reads the current focus entity without
  // re-creating the callback — mirrors pageContextRef [D1].
  const langflowFocusEntityHintRef = useRef(langflowFocusEntityHint);
  useEffect(() => {
    langflowFocusEntityHintRef.current = langflowFocusEntityHint;
  }, [langflowFocusEntityHint]);

  const dockShellContext = useMemo(() => {
    if (!shellContextEmbedded) return null;
    const overlayPayload = salesTargetOverlay
      ? {
          active_modal: salesTargetOverlay.modal,
          active_modal_title:
            salesTargetOverlay.mode === 'edit' ? 'Edit quota' : 'Set quota',
          active_modal_context: salesTargetOverlay as Record<string, unknown>,
        }
      : null;
    return buildDockShellContext(
      { path: pageContext?.path ?? '', title: pageContext?.title },
      dockShellRouteMeta,
      assistantWidgetContext,
      overlayPayload
    );
  }, [
    shellContextEmbedded,
    pageContext?.path,
    pageContext?.title,
    dockShellRouteMeta,
    assistantWidgetContext,
    salesTargetOverlay,
  ]);

  const langflowContextQueryTypeResolved = useMemo(() => {
    const streamTopic = lastStreamRouting?.topicSlug?.trim() || pendingTopicSlug.trim();
    if (streamTopic) return streamTopic;
    // Chart suggestion prefill fills the composer — still resolve as widget when chart context is active [2.2.1355].
    if (shellContextEmbedded && (assistantWidgetContext?.contextWidgetId ?? '').trim()) {
      return 'widget';
    }
    const draft = langflowPageContextMessage.trim();
    if (draft) return langflowContextQueryType;
    if (shellContextEmbedded && messages.length === 0) {
      return inferDockIdleContextQueryType(
        pageContext?.path ?? dockShellRouteMeta.route,
        assistantWidgetContext
      );
    }
    return langflowContextQueryType;
  }, [
    lastStreamRouting?.topicSlug,
    pendingTopicSlug,
    langflowPageContextMessage,
    langflowContextQueryType,
    shellContextEmbedded,
    messages.length,
    pageContext?.path,
    dockShellRouteMeta.route,
    assistantWidgetContext,
  ]);
  /** Outbound stream prefix for THIS turn only (draft-inferred). Never sticky-prepend prior complete.topic_slug. */
  const langflowStreamQueryTypePrefix = useMemo(() => {
    const draftTopic = pendingTopicSlug.trim();
    if (draftTopic) return draftTopic;
    if (shellContextEmbedded && (assistantWidgetContext?.contextWidgetId ?? '').trim()) {
      return 'widget';
    }
    return undefined;
  }, [
    pendingTopicSlug,
    shellContextEmbedded,
    assistantWidgetContext?.contextWidgetId,
  ]);
  const brainStripTopicSlug =
    lastStreamRouting?.topicSlug?.trim() || pendingTopicSlug.trim();

  const {
    context: langflowBrainContext,
    loading: langflowBrainContextLoading,
    learningCatchUpHint: langflowLearningCatchUpHint,
    dismissLearningCatchUpHint: dismissLangflowLearningCatchUpHint,
    refreshWithCatchup: refreshLangflowBrainContext,
  } = useAssistantContext({
    threadId: activeThreadId,
    userMessage: langflowPageContextMessage,
    queryType: langflowContextQueryTypeResolved,
    queryTypeRef: contextQueryTypeRef,
    enabled: langflowBrainContextEnabled,
    debounceMs: messages.length === 0 ? 200 : 650,
    followUpRefreshDelaysMs: [5000, 15000],
    focusEntityHint: langflowFocusEntityHint,
    shellContext: dockShellContext,
  });

  const welcomeChips = useMemo(
    () => collectAssistantWelcomeChips(langflowBrainContext),
    [langflowBrainContext],
  );

  const dockIdleStarterChips = useMemo(() => {
    if (!shellContextEmbedded || messages.length > 0) return [];
    return collectDockIdleStarterChips({
      pagePath: pageContext?.path ?? '',
      pageTitle: pageContext?.title ?? getAssistantPageHeadline(pageContext ?? undefined),
      widgetTitle: assistantWidgetContext?.widgetTitle,
      focusEntityName: langflowBrainContext?.focus_entity?.display_name,
      brainContext: langflowBrainContext,
      maxChips: 3,
    });
  }, [
    dockEmbedded,
    shellContextEmbedded,
    messages.length,
    pageContext?.path,
    pageContext?.title,
    assistantWidgetContext?.widgetTitle,
    langflowBrainContext,
  ]);

  const dockComposerPlaceholder = useMemo(() => {
    if (!shellContextEmbedded) return 'Ask anything…';
    const prefill = (prefillContext ?? '').trim();
    const draft = input.trim();
    const hasQuotedSelectionPrefill =
      prefill.includes('[Quoted selection') || draft.includes('[Quoted selection');
    return dockIdleComposerPlaceholder({
      pagePath: pageContext?.path ?? '',
      pageTitle: pageContext?.title ?? getAssistantPageHeadline(pageContext ?? undefined),
      widgetTitle: assistantWidgetContext?.widgetTitle,
      focusEntityName: langflowBrainContext?.focus_entity?.display_name,
      hasQuotedSelectionPrefill,
      overlayContext: salesTargetOverlay
        ? {
            active_modal: salesTargetOverlay.modal,
            active_modal_title:
              salesTargetOverlay.mode === 'edit' ? 'Edit quota' : 'Set quota',
          }
        : null,
    });
  }, [
    shellContextEmbedded,
    pageContext,
    prefillContext,
    input,
    assistantWidgetContext?.widgetTitle,
    langflowBrainContext?.focus_entity?.display_name,
    salesTargetOverlay,
  ]);

  useEffect(() => {
    if (!langflowBrainContextEnabled) return;
    return subscribeAssistantBrainContextRefresh(() => {
      void refreshLangflowBrainContext();
    });
  }, [langflowBrainContextEnabled, refreshLangflowBrainContext]);

  /** Re-resolve brain context immediately when the user navigates with the dock open [2.2.1347]. */
  const dockRouteContextKey = useMemo(() => {
    if (!shellContextEmbedded) return '';
    return [
      pageContext?.path ?? '',
      dockShellRouteMeta.route,
      dockShellRouteMeta.selectedRecord ?? '',
      assistantWidgetContext?.contextWidgetId ?? '',
    ].join('|');
  }, [
    shellContextEmbedded,
    pageContext?.path,
    dockShellRouteMeta.route,
    dockShellRouteMeta.selectedRecord,
    assistantWidgetContext?.contextWidgetId,
  ]);
  const prevDockRouteContextKeyRef = useRef('');
  useEffect(() => {
    if (!shellContextEmbedded || !langflowBrainContextEnabled || !dockRouteContextKey) return;
    if (prevDockRouteContextKeyRef.current === dockRouteContextKey) return;
    const hadPrior = prevDockRouteContextKeyRef.current.length > 0;
    prevDockRouteContextKeyRef.current = dockRouteContextKey;
    if (hadPrior) {
      void refreshLangflowBrainContext();
    }
  }, [
    shellContextEmbedded,
    dockRouteContextKey,
    langflowBrainContextEnabled,
    refreshLangflowBrainContext,
  ]);

  const appendAssistantNotice = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((prev) => [
      ...prev,
      {
        id: safeRandomUUID(),
        role: 'assistant' as const,
        content: trimmed,
        timestamp: new Date(),
      },
    ]);
  }, []);

  useEmailTemplateSavedCoaching(appendAssistantNotice);

  const executeRun = useCallback(
    async (
      trimmed: string,
      assistantId: string,
      sid: string,
      conversationForContext?: Message[],
      explicitContinuation?: Record<string, unknown> | null
    ): Promise<boolean> => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setStreamPhase({ assistantId, label: 'Thinking…' });
      setLastStreamRouting(null);
      setStreamSuggestedActions(undefined);
      contextQueryTypeRef.current = '';
      streamSuggestedActionsRef.current = undefined;
      setPendingTopicSlug(inferAssistantTopicFromMessage(trimmed));
      shellAssistant?.setOrchestrationRouting(null);

      const conversation = conversationForContext ?? messages;
      const apiWm = worldModelMarkdownRef.current.trim();
      const appendix =
        canPersistThreads
          ? buildAssistantThreadsWorldModelAppendix({
              sessionScope: threadPersistenceScope,
              flowId: persistenceFlowKey,
              activeThreadId,
              messages: conversation,
            }).trim()
          : '';
      const mergedWmRaw = [apiWm, appendix].filter(Boolean).join('\n\n---\n\n').trim();
      const mergedWm = mergedWmRaw.length > 0 ? mergedWmRaw : null;

      const body = buildLangflowBody(trimmed, sid, pageContextRef.current, mergedWm, {
        tenantId: user?.tenant_id,
        userId: user?.id,
        threadId: activeThreadId,
      });

      const applyAssistant = (patch: Partial<Message>) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m))
        );
      };

      try {
        let text: string;

        if (unifiedOrchestration && defaultAgentId) {
          const runAgentId =
            resolveAgentIdForAssistantContext(
              {
                path: pageContextRef.current?.path,
                title: pageContextRef.current?.title,
                prefillContext: prefillContext ?? undefined,
                draftMessage: trimmed,
              },
              user?.tenant_id
            ) ?? defaultAgentId;
          let pendingEnv =
            explicitContinuation !== undefined
              ? explicitContinuation
              : pendingOrchestrationContinuationRef.current;
          // [2.2.1590] Same-thread pivot off schedule invite confirm (Help me ask Contoso…).
          if (
            pendingEnv &&
            explicitContinuation === undefined &&
            shouldDropStaleOrchestrationContinuation(pendingEnv, trimmed)
          ) {
            pendingOrchestrationContinuationRef.current = null;
            pendingEnv = null;
          }
          const continuation =
            pendingEnv && Object.keys(pendingEnv).length > 0
              ? mergeOrchestrationContinuationForSend(pendingEnv, trimmed)
              : undefined;
          if (continuation && explicitContinuation === undefined) {
            pendingOrchestrationContinuationRef.current = null;
          }
          const streamMessage = resolveOperatorOutboundMessage(
            trimmed,
            operatorPendingPromptRef.current,
          );
          if (streamMessage !== trimmed) {
            operatorPendingPromptRef.current = undefined;
          }
          const outboundStreamMessage = buildAssistantConnectorOutboundMessage(
            streamMessage,
            connectorSelectionRef.current,
            connectorCatalogRef.current,
          );
          const turn = await streamOrchestrationAssistantText({
            message: outboundStreamMessage,
            threadId: activeThreadId,
            agentId: runAgentId,
            signal: ac.signal,
            continuation,
            queryTypePrefix: langflowStreamQueryTypePrefix,
            pagePath: pageContextRef.current?.path,
            pageTitle: (() => {
              const liveTitle =
                (typeof document !== 'undefined' ? document.title : undefined) ??
                pageContextRef.current?.title ??
                getAssistantPageHeadline(pageContextRef.current ?? undefined);
              return dealDisplayNameFromPageTitle(liveTitle)
                ? liveTitle
                : pageContextRef.current?.title ??
                    getAssistantPageHeadline(pageContextRef.current ?? undefined) ??
                    liveTitle;
            })(),
            canvasId: assistantWidgetContext?.contextWidgetId,
            // D1: scope the answer to the shell's live focus entity (open account/deal/etc.).
            focusEntityKind: langflowFocusEntityHintRef.current?.kind,
            focusEntityId: langflowFocusEntityHintRef.current?.entity_id,
            onAccumulated: (acc) => applyAssistant({ content: acc }),
            onActivity: (label) => setStreamPhase({ assistantId, label }),
            onRouting: (meta) => {
              const scoped =
                meta.contextQueryType?.trim() || meta.topicSlug?.trim() || '';
              if (scoped) contextQueryTypeRef.current = scoped;
              operatorPendingPromptRef.current = meta.operatorPendingPrompt?.trim() || undefined;
              streamSuggestedActionsRef.current = meta.suggestedNextActions;
              setStreamSuggestedActions(meta.suggestedNextActions);
              setLastStreamRouting(meta);
              setPendingTopicSlug('');
              shellAssistant?.setOrchestrationRouting(meta);
              refreshLangflowBrainContext();
              // Second refresh after React commits routing state (slow CI / dashboard dock).
              queueMicrotask(() => refreshLangflowBrainContext());
            },
            onNavigate: (path) => {
              if (path) navigate(path);
            },
          });
          text = turn.text;
          if (turn.pendingContinuation && Object.keys(turn.pendingContinuation).length > 0) {
            pendingOrchestrationContinuationRef.current = turn.pendingContinuation;
          }
          // data_result owns the confirm copy (Field/Value or prose-only) — avoid duplicate bubbles [2.2.1583].
          const assistantContent = turn.pendingDataResult
            ? ''
            : turn.pendingFormRequest
              ? text.trim()
              : text.trim() || '(empty response)';
          if (turn.clearPriorMessagePreview) {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id === assistantId) {
                  return {
                    ...m,
                    loading: false,
                    content: assistantContent,
                    ...(turn.pendingFormRequest ? { formRequest: turn.pendingFormRequest } : {}),
                    ...(turn.pendingDataResult ? { dataResult: turn.pendingDataResult } : {}),
                    ...(turn.pendingEmailTemplateChoice
                      ? { emailTemplateChoice: turn.pendingEmailTemplateChoice }
                      : {}),
                    ...(turn.pendingMessagePreview
                      ? { messagePreview: turn.pendingMessagePreview }
                      : { messagePreview: undefined }),
                  };
                }
                if (m.messagePreview) {
                  return { ...m, messagePreview: undefined };
                }
                return m;
              })
            );
          } else {
            applyAssistant({
              loading: false,
              content: assistantContent,
              ...(turn.pendingFormRequest ? { formRequest: turn.pendingFormRequest } : {}),
              ...(turn.pendingDataResult ? { dataResult: turn.pendingDataResult } : {}),
              ...(turn.pendingEmailTemplateChoice
                ? { emailTemplateChoice: turn.pendingEmailTemplateChoice }
                : {}),
              ...(turn.pendingMessagePreview ? { messagePreview: turn.pendingMessagePreview } : {}),
            });
          }
          if (turn.postSendCoaching?.trim()) {
            appendAssistantNotice(turn.postSendCoaching);
            triggerEmailTemplateLearningRefresh();
          }
          if (turn.pendingApproval) {
            setApprovalError(null);
            setPendingApproval(turn.pendingApproval);
          }
          clearAssistantQuotaExceeded();
          setSessionQuotaExceeded(false);
          return true;
        } else {
          text = await runLangflowChat({
            transport,
            flowId,
            body,
            stream: useStream,
            signal: ac.signal,
            onStreamAccumulated: (acc) => {
              applyAssistant({ content: acc });
            },
            onStreamActivity: (label) => {
              setStreamPhase({ assistantId, label });
            },
          });

          if (useStream && !text.trim()) {
            setStreamPhase({ assistantId, label: 'Fetching full response…' });
            text = await runLangflowChat({
              transport,
              flowId,
              body,
              stream: false,
              signal: ac.signal,
            });
          }
        }

        applyAssistant({
          loading: false,
          content: text.trim() ? text : '(empty response)',
        });
        clearAssistantQuotaExceeded();
        setSessionQuotaExceeded(false);
        return true;
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') {
          setStreamPhase(null);
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const content = m.content.trim() ? `${m.content}\n\n— Stopped` : 'Generation stopped.';
              return { ...m, loading: false, content, error: false };
            })
          );
          return false;
        }
        const msg = e instanceof Error ? e.message : String(e);
        if (isAssistantQuotaExceededError(msg)) {
          markAssistantQuotaExceeded();
          setSessionQuotaExceeded(true);
        }
        applyAssistant({ loading: false, content: msg, error: true });
        return false;
      } finally {
        setStreamPhase(null);
        if (abortRef.current === ac) abortRef.current = null;
        setIsLoading(false);
      }
    },
    [
      activeThreadId,
      canPersistThreads,
      defaultAgentId,
      flowId,
      messages,
      persistenceFlowKey,
      prefillContext,
      threadPersistenceScope,
      transport,
      unifiedOrchestration,
      useStream,
      user?.id,
      user?.tenant_id,
      shellAssistant,
      appendAssistantNotice,
      langflowStreamQueryTypePrefix,
    ]
  );

  const retryFailedAssistant = useCallback(
    async (assistantId: string) => {
      if (isLoading) return;
      const idx = messages.findIndex((x) => x.id === assistantId);
      if (idx <= 0) return;
      const userMsg = messages[idx - 1];
      if (userMsg.role !== 'user') return;
      const trimmed = userMsg.content.trim();
      if (!trimmed || !configured) return;

      setEditingUserId(null);
      setEditDraft('');
      setAwayFromBottom(false);
      const nextConversation = messages.map((m) =>
        m.id === assistantId ? { ...m, loading: true, error: false, content: '' } : m
      );
      setMessages(nextConversation);
      setIsLoading(true);
      await executeRun(trimmed, assistantId, sessionId, nextConversation);
    },
    [configured, executeRun, isLoading, messages, sessionId]
  );

  const dismissFormRequest = useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, formRequest: undefined } : m))
    );
  }, []);

  const sendMessageWithContinuation = useCallback(
    async (raw: string, continuation: Record<string, unknown>) => {
      const trimmed = raw.trim();
      if (!trimmed || isLoading || !configured) return;

      setEditingUserId(null);
      setEditDraft('');

      const assistantId = safeRandomUUID();
      const userId = safeRandomUUID();
      const userEntry: Message = {
        id: userId,
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      };
      const assistantEntry: Message = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        loading: true,
      };
      const nextConversation = [...messages, userEntry, assistantEntry];
      setAwayFromBottom(false);
      setMessages(nextConversation);
      setInput('');
      setIsLoading(true);
      setStreamPhase({ assistantId, label: 'Thinking…' });
      await executeRun(trimmed, assistantId, sessionId, nextConversation, continuation);
    },
    [configured, executeRun, isLoading, messages, sessionId]
  );

  const sendMessageWithText = useCallback(
    async (raw: string) => {
      const rawTrimmed = raw.trim();
      if (!rawTrimmed || isLoading) return;
      setEditingUserId(null);
      setEditDraft('');
      if (!configured) {
        if (quotaBlocked || isAssistantQuotaExceededError(flowResolveError ?? '')) {
          if (!quotaBlocked) {
            markAssistantQuotaExceeded();
            setSessionQuotaExceeded(true);
          }
          setMessages((prev) => [
            ...prev,
            { id: safeRandomUUID(), role: 'user', content: rawTrimmed, timestamp: new Date() },
            {
              id: safeRandomUUID(),
              role: 'assistant',
              content: assistantQuotaExceededBubbleMessage(),
              timestamp: new Date(),
              error: true,
            },
          ]);
          return;
        }
        const placeholderHint =
          envFlowIdRaw.trim() &&
          !isConcreteLangflowFlowId(envFlowIdRaw) &&
          looksLikeLangflowUuidCandidate(envFlowIdRaw)
            ? 'Replace VITE_LANGFLOW_FLOW_ID with your real Langflow flow UUID from the flow’s Settings (this value is not a valid id). '
            : '';
        const resolveHint =
          flowResolveError && !isConcreteLangflowFlowId(envFlowIdRaw) ? `${flowResolveError}\n\n` : '';
        const unifiedDeployHint =
          !unifiedOrchestration && (flowResolveError ?? '').includes('Perplexity')
            ? 'Cloud/Azure: rebuild brazn-frontend with VITE_ASSISTANT_UNIFIED_ORCHESTRATION_STREAM=true so the assistant uses the orchestration stream (lower latency). See domain/orchestration/backend/docs/assistant/LANGFLOW_MIGRATION_PROGRAM.md.\n\n'
            : '';
        setMessages((prev) => [
          ...prev,
          { id: safeRandomUUID(), role: 'user', content: rawTrimmed, timestamp: new Date() },
          {
            id: safeRandomUUID(),
            role: 'assistant',
            content: unifiedDeployHint + resolveHint + placeholderHint + LANGFLOW_CONFIG_HELP,
            timestamp: new Date(),
            error: true,
          },
        ]);
        setInput('');
        return;
      }

      const assistantId = safeRandomUUID();
      const userId = safeRandomUUID();
      const userEntry: Message = {
        id: userId,
        role: 'user',
        content: rawTrimmed,
        timestamp: new Date(),
      };
      const assistantEntry: Message = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        loading: true,
      };
      const nextConversation = [...messages, userEntry, assistantEntry];
      setAwayFromBottom(false);
      setMessages(nextConversation);
      setInput('');
      setIsLoading(true);
      setStreamPhase({ assistantId, label: 'Thinking…' });

      const ok = await executeRun(rawTrimmed, assistantId, sessionId, nextConversation);
      if (ok) {
        refreshLangflowBrainContext();
        if (looksLikePreferenceLearningMessage(rawTrimmed)) {
          setPreferenceSavedHint(true);
          notifyBrainPreferencesChanged();
        }
      }
    },
    [
      configured,
      envFlowIdRaw,
      executeRun,
      flowResolveError,
      isLoading,
      messages,
      refreshLangflowBrainContext,
      sessionId,
      unifiedOrchestration,
      variant,
    ]
  );

  const handlePanelContextActionClick = useCallback(
    (action: AssistantContextAction) => {
      if (isLoading) return;
      const resolved = resolveAssistantContextAction(action);
      if (resolved.kind === 'navigate') {
        navigate(resolved.path);
        return;
      }
      if (resolved.kind === 'open_email_template_create') {
        requestOpenEmailTemplateCreate({ coachInAssistant: true });
        return;
      }
      if (resolved.kind === 'open_sales_target_set') {
        requestOpenSalesTargetSet({ coachInAssistant: true });
        return;
      }
      const prompt = resolved.text.trim();
      if (!prompt) return;
      if (resolved.kind === 'send_prompt' || resolved.kind === 'prompt') {
        void sendMessageWithText(prompt);
      }
    },
    [isLoading, navigate, sendMessageWithText],
  );

  const handleWelcomeChipClick = useCallback(
    (chip: AssistantWelcomeChip) => {
      if (isLoading || flowResolving) return;
      if (chip.kind === 'open_create_modal') {
        requestOpenEmailTemplateCreate({ coachInAssistant: true });
        return;
      }
      void sendMessageWithText(chip.prompt);
    },
    [flowResolving, isLoading, sendMessageWithText],
  );

  const handleRunInBrowser = useCallback(() => {
    runBrowserLaunchFromAssistant(navigate, {
      instruction: input.trim(),
      prefillContext: prefillContext ?? undefined,
      pagePath: pageContext?.path ?? '',
      pageTitle: pageContext?.title,
      suggestedPrompts,
      onBeforeNavigate: () => {
        if (dockEmbedded) onClose();
      },
    });
  }, [
    dockEmbedded,
    input,
    navigate,
    onClose,
    pageContext?.path,
    pageContext?.title,
    prefillContext,
    suggestedPrompts,
  ]);

  useEffect(() => {
    sendMessageWithTextRef.current = (raw: string) => {
      void sendMessageWithText(raw);
    };
  }, [sendMessageWithText]);

  const applyPrefillPayload = useCallback((pre: AssistantPrefillPayload) => {
    const text = pre.markdown?.trim() ?? '';
    const wid = pre.contextWidgetId?.trim();
    if (wid) {
      const title = (pre.widgetTitle ?? '').trim() || 'Chart';
      setAssistantWidgetContext({ contextWidgetId: wid, widgetTitle: title });
    } else {
      setAssistantWidgetContext(null);
      setActiveWidgetContext(null);
    }
    if (!text) {
      requestAnimationFrame(() => textareaRef.current?.focus());
      return;
    }
    if (pre.autoSend) {
      pendingAutoSendRef.current = text;
      return;
    }
    setInput((prev) => (prev.trim() ? `${prev.trim()}\n\n${text}` : text));
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!subscribeGlobalPrefill) return;
    return subscribeAssistantOpenWithPrefill(() => {
      const pre = consumeAssistantPendingPrefill();
      if (pre) applyPrefillPayload(pre);
    });
  }, [subscribeGlobalPrefill, applyPrefillPayload]);

  useEffect(() => {
    if (!isOpen || !subscribeGlobalPrefill) return;
    const pre = consumeAssistantPendingPrefill();
    if (pre) applyPrefillPayload(pre);
  }, [isOpen, subscribeGlobalPrefill, applyPrefillPayload]);

  useEffect(() => {
    const text = pendingAutoSendRef.current;
    if (!text || !isOpen || !configured || isLoading || flowResolving) return;
    pendingAutoSendRef.current = null;
    sendMessageWithTextRef.current(text);
  }, [isOpen, configured, isLoading, flowResolving, sendMessageWithText]);

  const regenerateLast = useCallback(async () => {
    if (isLoading) return;
    stopGeneration();
    setEditingUserId(null);
    setEditDraft('');
    setAwayFromBottom(false);
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    const trimmed = lastUser?.content?.trim();
    if (!trimmed || !configured) return;

    const assistantId = safeRandomUUID();
    let nextConversation: Message[] = [];
    setMessages((prev) => {
      const next = [...prev];
      while (next.length && next[next.length - 1]?.role === 'assistant') {
        next.pop();
      }
      next.push({
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        loading: true,
      });
      nextConversation = next;
      return next;
    });
    setIsLoading(true);
    await executeRun(trimmed, assistantId, sessionId, nextConversation);
  }, [configured, executeRun, isLoading, messages, sessionId, stopGeneration]);

  const commitEditedUserMessage = useCallback(
    async (userMessageId: string, raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed || !configured || isLoading) return;

      const assistantId = safeRandomUUID();
      setEditingUserId(null);
      setEditDraft('');
      setAwayFromBottom(false);

      let nextConversation: Message[] = [];
      setMessages((prev) => {
        const i = prev.findIndex((x) => x.id === userMessageId);
        if (i < 0) {
          nextConversation = prev;
          return prev;
        }
        const next = [...prev.slice(0, i)];
        next.push({
          ...prev[i],
          content: trimmed,
          timestamp: new Date(),
        });
        next.push({
          id: assistantId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          loading: true,
        });
        nextConversation = next;
        return next;
      });
      setIsLoading(true);
      await executeRun(trimmed, assistantId, sessionId, nextConversation);
    },
    [configured, executeRun, isLoading, sessionId]
  );

  const exportChat = useCallback(() => {
    if (!messages.length) return;
    const md = buildMarkdownTranscript(messages, sessionId, flowId);
    const day = new Date().toISOString().slice(0, 10);
    downloadMarkdownFile(`brazn-assistant-${day}.md`, md);
  }, [flowId, messages, sessionId]);

  const copyConversationToClipboard = useCallback(async () => {
    if (!messages.length) return;
    try {
      const md = buildMarkdownTranscript(messages, sessionId, flowId);
      await navigator.clipboard.writeText(md);
    } catch {
      /* ignore */
    }
  }, [flowId, messages, sessionId]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const ta = e.target as HTMLTextAreaElement;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (!isLoading && !flowResolving) void sendMessageWithText(ta.value);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && !flowResolving) void sendMessageWithText(ta.value);
    }
  };

  const copyBubble = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const canRegenerate =
    configured &&
    !isLoading &&
    !flowResolving &&
    messages.length > 0 &&
    messages[messages.length - 1]?.role === 'assistant' &&
    messages.some((m) => m.role === 'user');

  const lastUserMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') return messages[i].id;
    }
    return null;
  }, [messages]);

  const threadHistoryMenuRows = useMemo(() => {
    const activeInList = threadHistoryEntries.some((e) => e.threadId === activeThreadId);
    const synthetic: ThreadHistoryEntry[] = activeInList
      ? []
      : [
          {
            threadId: activeThreadId,
            preview: messages.some((m) => !m.loading) ? 'Current chat' : 'New chat',
            updatedAt: new Date().toISOString(),
          },
        ];
    const merged = [...synthetic, ...threadHistoryEntries];
    const seen = new Set<string>();
    return merged.filter((row) => {
      if (seen.has(row.threadId)) return false;
      seen.add(row.threadId);
      return true;
    });
  }, [threadHistoryEntries, activeThreadId, messages]);

  const pageContextHeadline = useMemo(
    () => getAssistantPageHeadline(pageContext ?? undefined),
    [pageContext]
  );

  const dockPageContextHref = useMemo(() => {
    const raw = pageContext?.path?.trim();
    if (!raw) return '';
    return raw.startsWith('/') ? raw : `/${raw}`;
  }, [pageContext?.path]);

  const starterPrompts = useMemo(
    () => getAssistantStarterPrompts(pageContext ?? undefined),
    [pageContext]
  );

  const followUpChipItems = useMemo(
    () =>
      collectContextFollowUpChipItems(
        langflowBrainContext,
        pageContext ?? undefined,
        3,
        lastStreamRouting?.suggestedNextActions ?? streamSuggestedActions ?? streamSuggestedActionsRef.current,
      ),
    [langflowBrainContext, pageContext, lastStreamRouting?.suggestedNextActions, streamSuggestedActions],
  );

  useEffect(() => {
    if (!editingUserId) return;
    const t = window.setTimeout(() => editTextareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [editingUserId]);

  const drawerOpen = isDrawer && isOpen;
  const backdropZ = isEmbeddedDrawer ? 'z-10' : elevatedStack ? 'z-[140]' : 'z-[105]';
  const panelZ = isEmbeddedDrawer ? 'z-20' : elevatedStack ? 'z-[145]' : 'z-[110]';
  const panelMaxW = isEmbeddedDrawer ? 'w-full max-w-none' : elevatedStack ? 'max-w-[400px]' : 'max-w-md';
  const shellDrawerStyle = isDrawer && !isEmbeddedDrawer ? shellDrawerPanelStyle : undefined;

  const isPageLanding = variant === 'page' && !shellContextEmbedded && messages.length === 0;
  const isImmersiveAssistant = Boolean(railEmbedded);

  const pageComposerBrainStrip =
    variant === 'page' &&
    unifiedOrchestration &&
    !(isPageLanding && hasProactiveAdvisorWelcome(langflowBrainContext)) ? (
      <LangflowAssistantBrainStrip
        context={langflowBrainContext}
        learningCatchUpHint={langflowLearningCatchUpHint}
        onDismissCatchUp={dismissLangflowLearningCatchUpHint}
        pageLabel={pageContextHeadline || 'Assistant'}
        activeTopicSlug={brainStripTopicSlug}
        topicPreviewPending={Boolean(pendingTopicSlug.trim()) && isLoading}
        compact
        hidePageLabel={isPageLanding}
        hideFocusLabel={isPageLanding}
      />
    ) : null;

  const perplexityComposer = (
    <AssistantPerplexityComposer
      input={input}
      onInputChange={setInput}
      onKeyDown={onKeyDown}
      onSend={() => void sendMessageWithText(input)}
      onSendText={(text) => void sendMessageWithText(text)}
      textareaRef={textareaRef}
      placeholder={flowResolving ? 'Resolving assistant flow…' : 'Ask anything…'}
      disabled={isLoading || flowResolving}
      isLoading={isLoading}
      onStop={stopGeneration}
      composerMaxHeightPx={composerMaxHeightPx}
      browserAutomationEnabled={browserAutomationEnabled}
      onRunInBrowser={handleRunInBrowser}
      topSlot={pageComposerBrainStrip}
      modeLabel="Assistant"
      connectorCatalog={connectorCatalog}
      connectorsLoading={connectorsLoading}
      connectorsError={connectorsError}
      connectorActiveCount={connectorActiveCount}
      isConnectorSelected={isConnectorSelected}
      onToggleConnector={toggleConnector}
      immersive={isImmersiveAssistant}
    />
  );

  const aiPanelMarkup = (
    <>
      {isDrawer && (
        <div
          data-testid="ai-panel-backdrop"
          className={[
            isEmbeddedDrawer
              ? `absolute inset-0 ${backdropZ} bg-gray-900/40 backdrop-blur-[1px] transition-opacity dark:bg-black/45`
              : `fixed inset-0 ${backdropZ} bg-gray-900/50 backdrop-blur-[1px] transition-opacity dark:bg-black/60`,
            drawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
          ].join(' ')}
          aria-hidden={!drawerOpen}
          onClick={onClose}
        />
      )}

      <aside
        id="brazn-ai-assistant-panel"
        data-testid="assistant-panel"
        role={isDrawer ? (drawerOpen ? 'dialog' : undefined) : 'dialog'}
        aria-modal={isDrawer && drawerOpen ? 'true' : undefined}
        aria-label="AI Assistant"
        className={[
          isDrawer
            ? isEmbeddedDrawer
              ? `relative ${panelZ} flex max-h-[min(48vh,520px)] min-h-[200px] w-full ${panelMaxW} flex-col overflow-hidden border-t border-gray-200 bg-white shadow-[0_-10px_36px_rgba(0,0,0,0.14)] transition-transform duration-300 ease-out dark:border-gray-700 dark:bg-gray-800`
              : [
                  `fixed right-0 ${panelZ} flex h-full w-full ${panelMaxW} flex-col border-l border-gray-200 bg-white shadow-2xl transition-transform duration-300 ease-out dark:border-gray-700 dark:bg-gray-800`,
                  drawerOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none',
                ].join(' ')
            : [
                'relative z-10 flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden shadow-none',
                dockEmbedded
                  ? 'h-full min-h-0 rounded-none border-0 bg-transparent p-0 ring-0 dark:bg-transparent'
                  : railEmbedded
                    ? 'h-full min-h-0 rounded-none border-0 bg-transparent p-0 ring-0 dark:bg-transparent'
                    : `relative rounded-none border-0 ${ASSISTANT_PAGE_BG_CLASS}`,
              ].join(' '),
        ].join(' ')}
        style={shellDrawerStyle}
      >
        {!dockEmbedded ? (
        <header
          className={
            isPageLanding
              ? isImmersiveAssistant
                ? 'absolute right-0 top-0 z-20 flex shrink-0 items-center justify-end gap-0.5 px-3 py-2'
                : 'absolute right-0 top-0 z-20 flex shrink-0 items-center justify-end gap-1 px-4 py-3'
              : variant === 'page'
                ? `flex shrink-0 items-start justify-between gap-2 border-b border-gray-200 ${ASSISTANT_PAGE_BG_CLASS} px-4 py-3 dark:border-gray-700`
                : 'flex shrink-0 items-start justify-between gap-2 border-b border-gray-200 bg-gradient-to-r from-primary-600/12 via-white to-white px-4 py-3 dark:border-gray-700 dark:from-primary-500/15 dark:via-gray-900 dark:to-gray-900'
          }
        >
          {!isPageLanding ? (
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <SparklesIcon className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">AI Assistant</h2>
              {variant !== 'page' && langflowConn?.phase === 'down' && (
                <span
                  className="max-w-[10rem] truncate rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
                  title={langflowConn.hint ?? 'Assistant backend not reachable'}
                >
                  Offline
                </span>
              )}
            </div>
          </div>
          ) : (
            <span className="sr-only">AI Assistant</span>
          )}
          <div className="relative flex shrink-0 items-center gap-1">
            {isImmersiveAssistant && isPageLanding ? (
              <button
                type="button"
                onClick={() => {
                  if (canPersistThreads) {
                    setThreadHistoryOpen((o) => !o);
                  } else {
                    onClose();
                  }
                }}
                data-testid="assistant-immersive-menu"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                aria-label="Assistant menu"
                aria-expanded={threadHistoryOpen}
              >
                <EllipsisHorizontalIcon className="h-5 w-5" />
              </button>
            ) : null}
            {!isImmersiveAssistant || !isPageLanding ? (
              <>
            {isLoading && (
              <button
                type="button"
                onClick={stopGeneration}
                className="rounded-lg p-2 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/40"
                aria-label="Stop generation"
              >
                <StopCircleIcon className="h-5 w-5" />
              </button>
            )}
            {messages.length > 0 && (
              <button
                type="button"
                onClick={exportChat}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                aria-label="Export chat as Markdown"
                title="Export .md"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            )}
            {messages.length > 0 && (
              <button
                type="button"
                onClick={() => void copyConversationToClipboard()}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                aria-label="Copy conversation as Markdown"
                title="Copy all messages (Markdown)"
              >
                <ClipboardDocumentListIcon className="h-5 w-5" />
              </button>
            )}
            {canPersistThreads && (
              <button
                type="button"
                onClick={openNewAssistantThread}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                aria-label="New chat"
                title="New chat"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            )}
            {messages.length > 0 && (
              <button
                type="button"
                onClick={clearChat}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                aria-label="Clear chat"
                title="Clear chat"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            )}
            {canPersistThreads && (
              <div className="relative">
                <button
                  ref={threadHistoryButtonRef}
                  type="button"
                  onClick={() => setThreadHistoryOpen((o) => !o)}
                  className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  aria-label="Thread history"
                  title="Past threads"
                  aria-expanded={threadHistoryOpen}
                  aria-haspopup="menu"
                  aria-controls="brazn-ai-assistant-thread-history"
                >
                  <RectangleStackIcon className="h-5 w-5" />
                </button>
                {threadHistoryOpen && (
                  <div
                    ref={threadHistoryMenuRef}
                    id="brazn-ai-assistant-thread-history"
                    role="menu"
                    aria-label="Thread history"
                    className="absolute right-0 top-full z-[60] mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white py-2 shadow-xl dark:border-gray-600 dark:bg-gray-800"
                  >
                    <div className="border-b border-gray-100 px-2 pb-2 dark:border-gray-800">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={openNewAssistantThread}
                        className="w-full rounded-lg px-2 py-2 text-left text-xs font-semibold text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-950/50"
                      >
                        + New chat
                      </button>
                    </div>
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Recent threads
                    </p>
                    <ul className="max-h-56 overflow-y-auto px-1">
                      {threadHistoryMenuRows.length === 0 ? (
                        <li className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
                          No saved threads yet. Send a message to create one.
                        </li>
                      ) : (
                        threadHistoryMenuRows.map((row) => (
                          <li key={row.threadId}>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => switchToHistoryThread(row.threadId)}
                              className={[
                                'flex w-full flex-col gap-0.5 rounded-lg px-2 py-2 text-left text-xs transition',
                                row.threadId === activeThreadId
                                  ? 'bg-primary-50 text-gray-900 dark:bg-primary-950/40 dark:text-gray-100'
                                  : 'text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800',
                              ].join(' ')}
                            >
                              <span className="line-clamp-2 font-medium">{row.preview}</span>
                              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                {formatShortThreadTime(row.updatedAt)}
                              </span>
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {isDrawer ? (
              <button
                type="button"
                onClick={onClose}
                data-testid="assistant-close"
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                aria-label="Close assistant"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            ) : null}
              </>
            ) : null}
          </div>
        </header>
        ) : null}

        <div
          className={
            dockEmbedded
              ? 'relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden gap-0 p-0'
              : 'relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden gap-1 py-1.5 pl-0 pr-1.5'
          }
        >
          <div
            ref={scrollRef}
            onScroll={onScrollMessages}
            role="log"
            aria-live="polite"
            aria-label="Chat messages"
            className={[
              'app-shell-disappearing-scroll flex min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden',
              shellContextEmbedded && messages.length === 0
                ? 'shrink-0 flex-none gap-0 overflow-hidden'
                : shellContextEmbedded
                  ? 'min-h-0 flex-1 gap-1.5 overflow-x-hidden px-1.5 py-1 pr-4'
                  : isPageLanding
                    ? isImmersiveAssistant
                      ? `flex min-h-0 flex-1 flex-col overflow-hidden ${ASSISTANT_RAIL_BG_CLASS}`
                      : `flex min-h-0 flex-1 items-center justify-center px-4 ${ASSISTANT_PAGE_BG_CLASS}`
                    : variant === 'page'
                      ? `min-w-0 flex-1 gap-4 px-4 py-4 pb-8 ${ASSISTANT_PAGE_BG_CLASS}`
                      : 'min-w-0 flex-1 gap-4 bg-gray-50/70 px-4 py-4 pb-8 dark:bg-gray-950/40',
            ].join(' ')}
          >
          {messages.length === 0 &&
            !shellContextEmbedded &&
            (isPageLanding ? (
              <AssistantPageLanding
                compact={railEmbedded}
                immersive={isImmersiveAssistant}
                composer={perplexityComposer}
                welcomeChips={
                  welcomeChips.length > 0
                    ? welcomeChips
                    : langflowBrainContextLoading
                      ? []
                      : starterPrompts.map((q) => ({ label: q, prompt: q, kind: 'prompt' as const }))
                }
                onWelcomeChipClick={handleWelcomeChipClick}
                chipsDisabled={isLoading || flowResolving}
                chipsLoading={langflowBrainContextLoading && welcomeChips.length === 0}
                flowResolving={flowResolving}
                quotaChip={
                  quotaBannerState.visible ? (
                    <button
                      type="button"
                      data-testid="assistant-quota-welcome-chip"
                      disabled={isLoading || flowResolving}
                      onClick={() => navigate(ASSISTANT_QUOTA_SETTINGS_PATH)}
                      className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-950 shadow-sm hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
                    >
                      Review AI usage limits
                    </button>
                  ) : null
                }
                proactiveWelcome={
                  hasProactiveAdvisorWelcome(langflowBrainContext) ? (
                    <AssistantProactiveAdvisorWelcome
                      context={langflowBrainContext}
                      loading={langflowBrainContextLoading && !langflowBrainContext}
                      compact
                      contextActionChipsHostedExternally={contextActionChipsHostedExternally}
                      onSuggestedPrompt={(prompt) => {
                        if (isLoading) return;
                        void sendMessageWithText(prompt);
                      }}
                      onContextAction={handlePanelContextActionClick}
                    />
                  ) : undefined
                }
              />
            ) : flowResolving ? (
              <div
                data-testid="ai-empty-state"
                className="flex flex-1 flex-col items-center justify-center gap-4 py-8 text-center text-gray-500 dark:text-gray-400"
              >
                <SparklesIcon className="h-12 w-12 text-primary-500/70 dark:text-primary-400/70" aria-hidden />
                <p className="max-w-xs text-sm font-medium text-sky-800 dark:text-sky-200">
                  Connecting — looking up your tenant’s Langflow flow…
                </p>
              </div>
            ) : hasProactiveAdvisorWelcome(langflowBrainContext) ? (
              <AssistantProactiveAdvisorWelcome
                context={langflowBrainContext}
                loading={langflowBrainContextLoading && !langflowBrainContext}
                compact
                contextActionChipsHostedExternally={contextActionChipsHostedExternally}
                onSuggestedPrompt={(prompt) => {
                  if (isLoading) return;
                  void sendMessageWithText(prompt);
                }}
                onContextAction={handlePanelContextActionClick}
              />
            ) : (
              <div
                data-testid="ai-empty-state"
                className="flex flex-1 flex-col items-center justify-center gap-4 py-8 text-center text-gray-500 dark:text-gray-400"
              >
                <SparklesIcon className="h-12 w-12 text-primary-500/70 dark:text-primary-400/70" aria-hidden />
                <p className="max-w-md text-sm font-medium text-gray-700 dark:text-gray-300">
                  {assistantWelcomeSubtitle()}
                </p>
                {langflowBrainContextLoading && welcomeChips.length === 0 ? (
                  <p
                    className="text-xs text-gray-400 dark:text-gray-500"
                    data-testid="assistant-welcome-chips-loading"
                  >
                    Loading personalized suggestions…
                  </p>
                ) : null}
                <div className="flex w-full max-w-sm flex-col gap-2">
                  {quotaBannerState.visible ? (
                    <button
                      type="button"
                      data-testid="assistant-quota-welcome-chip"
                      disabled={isLoading || flowResolving}
                      onClick={() => navigate(ASSISTANT_QUOTA_SETTINGS_PATH)}
                      className="w-full rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm font-medium text-amber-950 shadow-sm hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-950/60"
                    >
                      Review AI usage limits
                    </button>
                  ) : null}
                  {(welcomeChips.length > 0
                    ? welcomeChips
                    : langflowBrainContextLoading
                      ? []
                      : starterPrompts.map((q) => ({ label: q, prompt: q, kind: 'prompt' as const }))
                  ).map((chip) => (
                    <AssistantWelcomeChipButton
                      key={chip.label}
                      chip={chip}
                      disabled={isLoading || flowResolving}
                      onClick={() => handleWelcomeChipClick(chip)}
                      size="sm"
                      className="w-full rounded-xl border-gray-200 bg-white shadow-sm hover:border-primary-300 hover:bg-primary-50/80 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-primary-600 dark:hover:bg-primary-950/30"
                    />
                  ))}
                </div>
              </div>
            ))}

          {messages.map((m, idx) => {
            const isLast = idx === messages.length - 1;
            const { displayBody, structuredSources } =
              m.role === 'assistant'
                ? partitionAssistantContent(m.content)
                : { displayBody: m.content, structuredSources: [] };
            const extraSourceUrls =
              m.role === 'assistant' && !m.error
                ? extraMarkdownSourceUrls(displayBody, structuredSources)
                : [];
            const showSources =
              m.role === 'assistant' &&
              !m.error &&
              !m.loading &&
              (structuredSources.length > 0 || extraSourceUrls.length > 0);
            const showFollowUps =
              isLast &&
              m.role === 'assistant' &&
              !m.loading &&
              followUpChipItems.length > 0 &&
              (!m.error ? Boolean(displayBody.trim()) : true);
            const emailDraft =
              m.role === 'assistant' &&
              !m.loading &&
              !m.error &&
              !m.messagePreview &&
              !m.emailTemplateChoice &&
              !m.dataResult &&
              !m.formRequest
                ? parseAssistantEmailDraft(displayBody)
                : null;

            return (
              <div
                key={m.id}
                className={
                  m.role === 'user'
                    ? `flex w-full min-w-0 items-end justify-end ${dockEmbedded ? 'gap-1.5' : 'gap-2'}`
                    : `flex w-full min-w-0 items-end justify-start ${dockEmbedded ? 'gap-1.5' : 'gap-2'}`
                }
              >
                {m.role === 'user' ? null : (
                  <div
                    className={[
                      'flex shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      dockEmbedded ? 'h-5 w-5 text-[9px]' : 'h-8 w-8',
                      dockEmbedded
                        ? 'bg-gradient-to-br from-primary-100 to-violet-100 text-primary-700 dark:from-primary-950/60 dark:to-violet-950/40 dark:text-primary-200'
                        : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-100',
                    ].join(' ')}
                    aria-hidden
                  >
                    ✦
                  </div>
                )}
                <div
                  className={`group relative min-w-0 ${
                    dockEmbedded ? 'max-w-[calc(100%-2rem)]' : 'max-w-[85%]'
                  }`}
                >
                  {m.role === 'user' && editingUserId === m.id ? (
                    <div className="rounded-2xl border border-primary-400 bg-white p-3 shadow-md dark:border-primary-500 dark:bg-gray-950">
                      <textarea
                        ref={editTextareaRef}
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={5}
                        className="w-full resize-y rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            void commitEditedUserMessage(m.id, editDraft);
                          }
                        }}
                        aria-label="Edit message"
                      />
                      <div className="mt-2 flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingUserId(null);
                            setEditDraft('');
                          }}
                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={!editDraft.trim()}
                          onClick={() => void commitEditedUserMessage(m.id, editDraft)}
                          className="rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
                        >
                          Save & resend
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {(displayBody.trim() ||
                        m.loading ||
                        m.error ||
                        (!m.dataResult &&
                          !m.emailTemplateChoice &&
                          !m.messagePreview &&
                          !m.formRequest)) && (
                      <div
                        className={[
                          'max-w-full min-w-0 rounded-xl text-sm shadow-sm',
                          dockEmbedded ? 'px-2 py-1.5' : 'px-3 py-2',
                          m.role === 'user'
                            ? 'bg-primary-600 text-white ring-1 ring-primary-500/20 dark:bg-primary-600'
                            : m.error
                              ? 'border border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200'
                              : dockEmbedded
                                ? 'border-0 bg-white text-gray-900 ring-1 ring-gray-200/70 dark:bg-gray-800/95 dark:text-gray-100 dark:ring-gray-700/50'
                                : 'border border-gray-200 bg-gray-50 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100',
                        ].join(' ')}
                        data-testid={m.role === 'assistant' ? 'assistant-message-body' : undefined}
                        aria-busy={m.role === 'assistant' && m.loading ? true : undefined}
                      >
                        {m.role === 'user' ? (
                          <span className="block whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                            {m.content}
                          </span>
                        ) : m.loading && !m.content.trim() ? (
                          <AssistantThinkingIndicator
                            label={(() => {
                              const phase =
                                streamPhase && m.id === streamPhase.assistantId
                                  ? streamPhase.label.trim()
                                  : '';
                              if (phase.startsWith('Running ') || phase.startsWith('Routing:')) {
                                return 'Processing…';
                              }
                              if (phase === 'Processing…' || phase === 'Fetching full response…') {
                                return phase;
                              }
                              return phase || 'Thinking…';
                            })()}
                            detail={(() => {
                              const phase =
                                streamPhase && m.id === streamPhase.assistantId
                                  ? streamPhase.label.trim()
                                  : '';
                              if (phase.startsWith('Running ') || phase.startsWith('Routing:')) {
                                return phase;
                              }
                              return undefined;
                            })()}
                          />
                        ) : !m.error ? (
                          <div>
                            {displayBody.trim().length > 0 ? (
                              <>
                                {emailDraft ? (
                                  <AssistantEmailDraftCard
                                    draft={emailDraft}
                                    disabled={isLoading || flowResolving}
                                    onImprove={(prompt) => void sendMessageWithText(prompt)}
                                    onSaveAsTemplate={(prefill) =>
                                      requestOpenEmailTemplateCreate({
                                        prefill,
                                        coachInAssistant: true,
                                        successHint: 'templates_library',
                                      })
                                    }
                                  />
                                ) : (
                                  <AiAssistantMarkdown content={displayBody} />
                                )}
                                {m.loading && m.content.trim().length > 0 && (
                                  <span className="inline-flex items-center gap-1" aria-label="Generating">
                                    <StreamingCaret />
                                  </span>
                                )}
                              </>
                            ) : m.loading ? (
                              <span className="text-[12px] text-gray-500 dark:text-gray-400">
                                Loading results…
                              </span>
                            ) : null}
                            {m.loading &&
                              streamPhase &&
                              m.id === streamPhase.assistantId &&
                              streamPhase.label.trim().length > 0 && (
                                <p className="mt-2 border-t border-gray-200/80 pt-2 text-[11px] font-medium leading-snug text-gray-500 dark:border-gray-600/80 dark:text-gray-400">
                                  {streamPhase.label}
                                </p>
                              )}
                          </div>
                        ) : (
                          <span
                            data-testid="ai-error-message"
                            className="whitespace-pre-wrap break-words"
                          >
                            {displayBody}
                          </span>
                        )}
                      </div>
                      )}
                      {m.role === 'assistant' && m.dataResult && (
                        <div className="mt-3" data-testid="assistant-data-result-wrap">
                          <DataTableCard data={m.dataResult} />
                        </div>
                      )}
                      {m.role === 'assistant' && m.emailTemplateChoice && (
                        <div className="mt-3">
                          <EmailTemplateChoiceCard
                            choice={m.emailTemplateChoice}
                            onSelect={async (_id, title, continuation) => {
                              await sendMessageWithContinuation(
                                title,
                                (continuation as Record<string, unknown>) ?? {}
                              );
                            }}
                          />
                        </div>
                      )}
                      {m.role === 'assistant' && m.messagePreview && (
                        <div className="mt-3" data-testid="assistant-message-preview">
                          <MessagePreviewCard
                            messagePreview={m.messagePreview}
                            disabled={isLoading || flowResolving}
                            onPreviewUpdate={(updated) => {
                              setMessages((prev) =>
                                prev.map((msg) =>
                                  msg.id === m.id ? { ...msg, messagePreview: updated } : msg
                                )
                              );
                              const cont = updated.continuation as Record<string, unknown> | undefined;
                              if (cont) {
                                pendingOrchestrationContinuationRef.current = cont;
                              }
                            }}
                            onSaveAsTemplate={(prefill) =>
                              requestOpenEmailTemplateCreate({
                                prefill,
                                coachInAssistant: true,
                                successHint: 'templates_library',
                              })
                            }
                            onAction={async (action, continuation) => {
                              if (action === 'send_message') {
                                await sendMessageWithContinuation('send', {
                                  ...(continuation ?? { action: 'messaging', awaiting: 'confirmation' }),
                                });
                              } else if (
                                action === 'confirm_email_send' ||
                                action === 'schedule_meeting'
                              ) {
                                await sendMessageWithContinuation('yes', {
                                  ...(continuation ?? {}),
                                  ...(action === 'schedule_meeting'
                                    ? { confirm: true, awaiting: 'confirmation' }
                                    : {}),
                                });
                              } else if (action === 'cancel' || action === 'cancel_email_send') {
                                const cont = continuation as Record<string, unknown> | undefined;
                                const isSchedule =
                                  cont?.action === 'schedule_meeting' ||
                                  cont?.tool === 'schedule_meeting';
                                const notice =
                                  action === 'cancel_email_send'
                                    ? 'Email cancelled — nothing was sent.'
                                    : isSchedule
                                      ? 'Meeting not scheduled — cancelled.'
                                      : 'Cancelled — no action taken.';
                                setMessages((prev) =>
                                  prev.map((msg) =>
                                    msg.id === m.id
                                      ? { ...msg, messagePreview: undefined, content: notice }
                                      : msg
                                  )
                                );
                                pendingOrchestrationContinuationRef.current = null;
                              }
                            }}
                          />
                        </div>
                      )}
                      {m.role === 'assistant' && m.formRequest && (
                        <div className="mt-3" data-testid="assistant-form-request">
                          <AssistantFormRequestCard
                            formRequest={m.formRequest}
                            onSubmit={async (formData) => {
                              const cont = m.formRequest?.continuation;
                              const isFormCatalog = cont?.action === 'form_catalog';
                              const isMessaging =
                                m.formRequest?.entity === 'message' ||
                                m.formRequest?.action === 'compose';
                              const isWidget = m.formRequest?.entity === 'widget';
                              const continuation =
                                isFormCatalog && cont
                                  ? {
                                      ...cont,
                                      collected_fields: formData,
                                      missing_fields: cont.missing_fields ?? [],
                                    }
                                  : cont
                                    ? { ...cont, collected_fields: formData }
                                    : isMessaging
                                      ? {
                                          action: 'messaging',
                                          entity: 'message',
                                          collected_fields: formData,
                                        }
                                      : isWidget
                                        ? {
                                            action: 'widget',
                                            entity: 'widget',
                                            collected_fields: formData,
                                          }
                                        : {
                                            action: 'crud_create',
                                            entity: m.formRequest?.entity ?? 'deal',
                                            collected_fields: formData,
                                            missing_fields: [],
                                          };
                              const submitMsg =
                                isFormCatalog && cont
                                  ? getFormCatalogSubmitMessage(cont)
                                  : isMessaging
                                    ? 'Compose message with submitted details'
                                    : isWidget
                                      ? 'Create widget with submitted details'
                                      : getCrudCreateSubmitMessage(
                                          (continuation as { entity?: string }).entity ?? 'deal'
                                        );
                              await sendMessageWithContinuation(submitMsg, continuation);
                              if (isFormCatalog) dismissFormRequest(m.id);
                            }}
                            onCancel={() => dismissFormRequest(m.id)}
                            disabled={isLoading}
                          />
                        </div>
                      )}
                      {m.role === 'user' &&
                        m.id === lastUserMessageId &&
                        !isLoading &&
                        !editingUserId &&
                        (dockEmbedded ? (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingUserId(m.id);
                              setEditDraft(m.content);
                            }}
                            className="absolute -top-5 right-0 z-10 inline-flex items-center gap-0.5 rounded-xl px-1.5 py-0.5 text-[10px] font-medium text-white/90 opacity-0 shadow-sm transition hover:text-white group-hover:opacity-100"
                          >
                            <PencilSquareIcon className="h-3 w-3" aria-hidden />
                            Edit
                          </button>
                        ) : (
                          <div className="mt-1 flex justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingUserId(m.id);
                                setEditDraft(m.content);
                              }}
                              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-white/85 opacity-0 shadow-sm transition hover:text-white group-hover:opacity-100"
                            >
                              <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden />
                              Edit & resend
                            </button>
                          </div>
                        ))}
                    </>
                  )}

                  {m.role === 'assistant' && m.error && !m.loading && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {isAssistantQuotaExceededError(displayBody) ? (
                        <AssistantQuotaSettingsLink />
                      ) : null}
                      <button
                        type="button"
                        disabled={isLoading}
                        onClick={() => void retryFailedAssistant(m.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-800 shadow-sm hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-200 dark:hover:bg-red-950/40"
                        aria-label="Retry failed assistant response"
                      >
                        <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden />
                        Retry
                      </button>
                    </div>
                  )}

                  {showSources && (
                    <AiAssistantSources structured={structuredSources} extraUrls={extraSourceUrls} />
                  )}

                  {showFollowUps ? (
                    <AssistantMessageFollowUpChips
                      chips={followUpChipItems}
                      disabled={isLoading || flowResolving}
                      onSelect={(q) => void sendMessageWithText(q)}
                    />
                  ) : null}

                  {m.role === 'assistant' &&
                    !m.loading &&
                    !m.error &&
                    !emailDraft &&
                    displayBody.trim().length > 0 && (
                    <button
                      type="button"
                      onClick={() => void copyBubble(displayBody)}
                      className="absolute -right-1 -top-1 rounded-md bg-white p-1 text-gray-400 opacity-0 shadow-sm ring-1 ring-gray-200 transition hover:text-primary-600 group-hover:opacity-100 dark:bg-gray-800 dark:ring-gray-600 dark:hover:text-primary-400"
                      aria-label="Copy reply"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {m.role === 'user' ? (
                  <div
                    className={[
                      'flex shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      dockEmbedded ? 'h-5 w-5 text-[9px]' : 'h-8 w-8',
                      'bg-primary-100/90 text-primary-800 dark:bg-primary-900/70 dark:text-primary-200',
                    ].join(' ')}
                    aria-hidden
                  >
                    {dockEmbedded ? null : 'You'}
                  </div>
                ) : null}
              </div>
            );
          })}
          </div>

          {awayFromBottom && messages.length > 0 && (
            <button
              type="button"
              onClick={jumpToLatest}
              className={[
                'absolute z-20 inline-flex items-center gap-1.5 rounded-xl border border-primary-500/30 bg-primary-600 px-3 py-2 text-xs font-semibold text-white shadow-lg ring-1 ring-black/5 transition hover:bg-primary-700 dark:ring-white/10',
                dockEmbedded ? 'bottom-1.5 right-1.5' : 'bottom-4 right-4',
                isLoading ? 'animate-pulse' : '',
              ].join(' ')}
              aria-label="Jump to latest messages"
            >
              <ChevronDownIcon className="h-4 w-4" aria-hidden />
              Jump to latest
            </button>
          )}

        {isPageLanding ? (
        <footer
          className={
            variant === 'page'
              ? `shrink-0 border-0 px-4 pb-6 pt-2 ${ASSISTANT_PAGE_BG_CLASS}`
              : 'shrink-0 border-t border-gray-200/60 bg-white/95 px-1.5 py-1 pr-2 dark:border-gray-700/50 dark:bg-gray-800/95'
          }
        >
          {quotaBannerState.visible ? (
            <AssistantQuotaExceededBanner
              severity={quotaBannerState.severity}
              percentUsed={quotaBannerState.percentUsed}
              onDismiss={() => {
                if (quotaBannerState.isExceeded) {
                  clearAssistantQuotaExceeded();
                  setSessionQuotaExceeded(false);
                } else {
                  quotaBannerState.dismiss();
                }
              }}
            />
          ) : null}
          {variant === 'page' &&
          unifiedOrchestration &&
          !hasProactiveAdvisorWelcome(langflowBrainContext) ? (
            <LangflowAssistantBrainStrip
              context={langflowBrainContext}
              learningCatchUpHint={langflowLearningCatchUpHint}
              onDismissCatchUp={dismissLangflowLearningCatchUpHint}
              pageLabel={pageContextHeadline || 'Assistant'}
              activeTopicSlug={brainStripTopicSlug}
              topicPreviewPending={Boolean(pendingTopicSlug.trim()) && isLoading}
              compact={false}
            />
          ) : null}
          {variant === 'page' && unifiedOrchestration ? (
            <p
              className="sr-only"
              data-testid="assistant-stream-routing"
              data-topic-slug={brainStripTopicSlug || undefined}
              data-ttft-ms={
                typeof lastStreamRouting?.ttftMs === 'number'
                  ? String(Math.round(lastStreamRouting.ttftMs))
                  : undefined
              }
              data-total-ms={
                typeof lastStreamRouting?.totalMs === 'number'
                  ? String(Math.round(lastStreamRouting.totalMs))
                  : undefined
              }
              data-world-model-fetch-ms={
                typeof lastStreamRouting?.worldModelFetchMs === 'number'
                  ? String(Math.round(lastStreamRouting.worldModelFetchMs))
                  : undefined
              }
            >
              {brainStripTopicSlug ? `Topic: ${brainStripTopicSlug}` : 'topic routing'}
            </p>
          ) : null}
        </footer>
        ) : null}

        {!isPageLanding ? (
        <footer
          className={
            dockEmbedded
              ? 'shrink-0 border-t border-gray-200/60 bg-white/95 px-1.5 py-1 pr-2 dark:border-gray-700/50 dark:bg-gray-800/95'
              : variant === 'page'
                ? `shrink-0 border-0 px-4 pb-6 pt-2 ${ASSISTANT_PAGE_BG_CLASS}`
                : 'shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-3 dark:border-gray-700 dark:bg-gray-800/80'
          }
        >
          {variant !== 'page' && unifiedOrchestration ? (
            <LangflowAssistantBrainStrip
              context={langflowBrainContext}
              learningCatchUpHint={langflowLearningCatchUpHint}
              onDismissCatchUp={dismissLangflowLearningCatchUpHint}
              pageLabel=""
              activeTopicSlug={brainStripTopicSlug}
              topicPreviewPending={Boolean(pendingTopicSlug.trim()) && isLoading}
              compact={false}
            />
          ) : null}
          {canRegenerate && !dockEmbedded ? (
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                onClick={() => void regenerateLast()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:border-primary-300 hover:bg-primary-50/60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-500 dark:hover:bg-primary-950/40"
              >
                <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden />
                Regenerate last reply
              </button>
            </div>
          ) : null}
          {isDrawer && Boolean(pageContext?.path?.trim()) && (
            <div
              className="mb-2 min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-gray-600 dark:bg-gray-800/80"
              aria-label="Assistant page context"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Asking about
              </p>
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">
                {pageContextHeadline || 'This page'}
              </p>
              <p
                className="mt-0.5 truncate font-mono text-[11px] text-gray-500 dark:text-gray-400"
                title={pageContext?.path}
              >
                {pageContext?.path}
              </p>
              {(langflowBrainInGraph || worldModelContextLoaded || canPersistThreads) && (
                <p className="mt-1.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  {langflowBrainInGraph
                    ? 'Brain context and CRM tools run in the Langflow graph; this tab’s thread history is still attached'
                    : worldModelContextLoaded
                      ? 'Business / world-model snapshot plus this tab’s Langflow threads (current + recent history) are attached to each prompt'
                      : 'This tab’s Langflow threads (current + recent history) are attached to each prompt'}
                </p>
              )}
            </div>
          )}
          {assistantWidgetContext && !dockEmbedded ? (
            <div
              data-testid="assistant-widget-context-banner"
              className="mb-2 flex items-center gap-2 border-b border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
            >
              <span>Asking about:</span>
              <span className="font-medium">{assistantWidgetContext.widgetTitle}</span>
              <button
                type="button"
                onClick={() => {
                  setAssistantWidgetContext(null);
                  setActiveWidgetContext(null);
                }}
                className="ml-auto text-blue-400 hover:text-blue-600 dark:hover:text-blue-200"
                aria-label="Clear chart context"
              >
                ×
              </button>
            </div>
          ) : null}
          {quotaBannerState.visible ? (
            <AssistantQuotaExceededBanner
              severity={quotaBannerState.severity}
              percentUsed={quotaBannerState.percentUsed}
              onDismiss={() => {
                if (quotaBannerState.isExceeded) {
                  clearAssistantQuotaExceeded();
                  setSessionQuotaExceeded(false);
                } else {
                  quotaBannerState.dismiss();
                }
              }}
            />
          ) : null}
          {variant === 'page' && !shellContextEmbedded ? (
            <div className={`mx-auto w-full ${railEmbedded ? 'max-w-none px-2' : 'max-w-2xl'}`}>
              {perplexityComposer}
            </div>
          ) : (
          <div
            className={
              variant === 'page'
                ? dockEmbedded
                  ? 'overflow-hidden bg-transparent'
                  : 'overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800'
                : 'flex min-w-0 gap-2'
            }
          >
            {dockEmbedded ? (
              <div className="mb-0 flex items-center justify-between gap-1">
                <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                  {pageContextHeadline && dockPageContextHref ? (
                    <Link
                      to={dockPageContextHref}
                      className="inline-flex min-w-0 max-w-[14rem] items-center gap-1 truncate rounded-lg border border-gray-200/80 bg-gray-50/90 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 transition hover:border-primary-200 hover:bg-primary-50/70 dark:border-gray-600/70 dark:bg-gray-900/90 dark:text-gray-200 dark:hover:border-primary-700 dark:hover:bg-primary-950/30"
                      data-testid="assistant-dock-page-context-link"
                      title={dockPageContextHref}
                      aria-label={`Open page context: ${pageContextHeadline}`}
                    >
                      <span className="text-gray-400 dark:text-gray-500" aria-hidden>
                        ◆
                      </span>
                      <span className="truncate">{pageContextHeadline}</span>
                    </Link>
                  ) : null}
                  {assistantWidgetContext ? (
                    <span
                      className="inline-flex min-w-0 max-w-[12rem] items-center gap-1 truncate rounded-xl border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
                      data-testid="assistant-dock-widget-context-chip"
                      title={assistantWidgetContext.widgetTitle}
                    >
                      <span className="truncate">{assistantWidgetContext.widgetTitle}</span>
                      <button
                        type="button"
                        className="shrink-0 text-blue-500 hover:text-blue-700 dark:hover:text-blue-100"
                        aria-label="Clear chart context"
                        onClick={() => {
                          setAssistantWidgetContext(null);
                          setActiveWidgetContext(null);
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ) : null}
                  {langflowBrainContext?.focus_entity?.display_name?.trim() ? (
                    <span
                      className="inline-flex min-w-0 max-w-[12rem] truncate rounded-xl border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-900/50 dark:text-slate-200"
                      data-testid="assistant-dock-focus-entity-chip"
                      title={langflowBrainContext.focus_entity?.display_name}
                    >
                      {langflowBrainContext.focus_entity.display_name.trim()}
                    </span>
                  ) : null}
                  {langflowBrainContextLoading && messages.length === 0 ? (
                    <span
                      className="shrink-0 text-[10px] text-gray-400 dark:text-gray-500"
                      data-testid="assistant-dock-context-loading"
                      aria-live="polite"
                    >
                      Updating context…
                    </span>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {isLoading ? (
                    <button
                      type="button"
                      onClick={stopGeneration}
                      className="rounded-xl p-1 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-950/40"
                      aria-label="Stop generation"
                    >
                      <StopCircleIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                  {canRegenerate ? (
                    <button
                      type="button"
                      onClick={() => void regenerateLast()}
                      className="rounded-xl p-1 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                      aria-label="Regenerate last reply"
                      title="Regenerate last reply"
                    >
                      <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden />
                    </button>
                  ) : null}
                  {canPersistThreads ? (
                    <button
                      type="button"
                      onClick={openNewAssistantThread}
                      className="rounded-xl p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                      aria-label="New chat"
                      title="New chat"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                  {canPersistThreads ? (
                    <div className="relative">
                      <button
                        ref={threadHistoryButtonRef}
                        type="button"
                        onClick={() => setThreadHistoryOpen((o) => !o)}
                        className="rounded-xl p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                        aria-label="Thread history"
                        title="Past threads"
                        aria-expanded={threadHistoryOpen}
                        aria-haspopup="menu"
                        aria-controls="brazn-ai-assistant-thread-history"
                      >
                        <RectangleStackIcon className="h-4 w-4" />
                      </button>
                      {threadHistoryOpen ? (
                        <div
                          ref={threadHistoryMenuRef}
                          id="brazn-ai-assistant-thread-history"
                          role="menu"
                          aria-label="Thread history"
                          className="absolute bottom-full right-0 z-[60] mb-1 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white py-2 shadow-xl dark:border-gray-600 dark:bg-gray-800"
                        >
                          <div className="border-b border-gray-100 px-2 pb-2 dark:border-gray-800">
                            <button
                              type="button"
                              role="menuitem"
                              onClick={openNewAssistantThread}
                              className="w-full rounded-lg px-2 py-2 text-left text-xs font-semibold text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-950/50"
                            >
                              + New chat
                            </button>
                          </div>
                          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Recent threads
                          </p>
                          <ul className="max-h-56 overflow-y-auto px-1">
                            {threadHistoryMenuRows.length === 0 ? (
                              <li className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
                                No saved threads yet. Send a message to create one.
                              </li>
                            ) : (
                              threadHistoryMenuRows.map((row) => (
                                <li key={row.threadId}>
                                  <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => switchToHistoryThread(row.threadId)}
                                    className={[
                                      'flex w-full flex-col gap-0.5 rounded-lg px-2 py-2 text-left text-xs transition',
                                      row.threadId === activeThreadId
                                        ? 'bg-primary-50 text-gray-900 dark:bg-primary-950/40 dark:text-gray-100'
                                        : 'text-gray-800 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800',
                                    ].join(' ')}
                                  >
                                    <span className="line-clamp-2 font-medium">{row.preview}</span>
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                      {formatShortThreadTime(row.updatedAt)}
                                    </span>
                                  </button>
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {composerTopSlot ? <div className="px-1">{composerTopSlot}</div> : null}
            {shellContextEmbedded && messages.length === 0 && dockIdleStarterChips.length > 0 ? (
              <div
                className="flex flex-wrap gap-1 rounded-xl border border-gray-200/60 bg-gray-50/40 px-1.5 py-1 dark:border-gray-700/40 dark:bg-gray-900/20"
                data-testid="assistant-dock-idle-chips"
              >
                {dockIdleStarterChips.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    data-testid="assistant-dock-idle-chip"
                    disabled={isLoading || flowResolving}
                    onClick={() => {
                      if (isLoading || flowResolving) return;
                      if (chip.contextAction) {
                        handlePanelContextActionClick(chip.contextAction);
                        return;
                      }
                      const livePageTitle =
                        pageContext?.title ??
                        getAssistantPageHeadline(pageContext ?? undefined) ??
                        (typeof document !== 'undefined' ? document.title : undefined);
                      const prompt = resolveDockIdleChipPromptAtSend(chip, {
                        pagePath: pageContext?.path ?? '',
                        pageTitle: livePageTitle,
                        focusEntityName: langflowBrainContext?.focus_entity?.display_name,
                      });
                      void sendMessageWithText(prompt);
                    }}
                    className="rounded-xl border border-gray-200/80 bg-white/95 px-2.5 py-1 text-[11px] font-medium text-gray-700 shadow-sm transition hover:border-primary-200 hover:bg-primary-50/70 disabled:opacity-50 dark:border-gray-600/70 dark:bg-gray-800/90 dark:text-gray-200 dark:hover:border-primary-700 dark:hover:bg-primary-950/30"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            ) : null}
            {variant === 'page' && unifiedOrchestration ? (
              dockEmbedded ? (
                messages.length === 0 ? (
                  <LangflowAssistantBrainStrip
                    context={langflowBrainContext}
                    learningCatchUpHint={langflowLearningCatchUpHint}
                    onDismissCatchUp={dismissLangflowLearningCatchUpHint}
                    pageLabel={pageContextHeadline || 'Assistant'}
                    activeTopicSlug={brainStripTopicSlug}
                    topicPreviewPending={Boolean(pendingTopicSlug.trim()) && isLoading}
                    compact
                    dockEmbedded
                    hidePageLabel
                    hideFocusLabel
                  />
                ) : null
              ) : (
                <div className="border-b border-gray-100 px-2 pt-2 dark:border-gray-700">
                  <LangflowAssistantBrainStrip
                    context={langflowBrainContext}
                    learningCatchUpHint={langflowLearningCatchUpHint}
                    onDismissCatchUp={dismissLangflowLearningCatchUpHint}
                    pageLabel={pageContextHeadline || 'Assistant'}
                    activeTopicSlug={brainStripTopicSlug}
                    topicPreviewPending={Boolean(pendingTopicSlug.trim()) && isLoading}
                    compact
                  />
                </div>
              )
            ) : null}
            <div
              className={
                variant === 'page'
                  ? dockEmbedded
                    ? 'flex min-w-0 gap-1.5'
                    : 'flex min-w-0 gap-2 p-2'
                  : 'flex min-w-0 flex-1 gap-2'
              }
            >
              <textarea
                ref={textareaRef}
                data-testid="assistant-composer-input"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  const ta = e.target;
                  ta.style.height = '0px';
                  const next = Math.min(ta.scrollHeight, composerMaxHeightPx);
                  ta.style.height = `${next}px`;
                  ta.style.overflowY = ta.scrollHeight > composerMaxHeightPx ? 'auto' : 'hidden';
                }}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={
                  flowResolving ? 'Resolving assistant flow…' : dockComposerPlaceholder
                }
                disabled={isLoading || flowResolving}
                className={
                  variant === 'page'
                    ? dockEmbedded
                      ? 'min-h-[1.75rem] min-w-0 flex-1 resize-none overflow-y-hidden rounded-lg border border-gray-200/60 bg-gray-50/80 px-2 py-1 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400/30 dark:border-gray-600/60 dark:bg-gray-900/40 dark:text-white dark:placeholder-gray-500 dark:focus:border-primary-500'
                      : 'max-h-[200px] min-h-[2.75rem] min-w-0 flex-1 resize-none overflow-y-auto border-0 bg-transparent px-1 py-1 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder-gray-500'
                    : 'max-h-[200px] min-h-[2.75rem] min-w-0 flex-1 resize-none overflow-y-auto rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-inner focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400/25'
                }
              />
              {browserAutomationEnabled ? (
                <button
                  type="button"
                  data-testid="assistant-run-in-browser"
                  onClick={handleRunInBrowser}
                  disabled={isLoading || flowResolving}
                  className={[
                    'self-end rounded-xl border border-sky-200 bg-sky-50 font-semibold text-sky-900 transition hover:bg-sky-100 disabled:opacity-50 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-100 dark:hover:bg-sky-900/50',
                    dockEmbedded
                      ? 'px-2 py-1 text-[11px]'
                      : 'px-3 py-2 text-xs',
                  ].join(' ')}
                  title="Run in AI Browser (LinkedIn, Salesforce, CRM)"
                >
                  Browser
                </button>
              ) : null}
              <button
                type="button"
                data-testid="assistant-send-button"
                onClick={() => void sendMessageWithText(input)}
                disabled={isLoading || flowResolving || !input.trim()}
                className={[
                  'self-end rounded-xl bg-primary-600 font-semibold text-white transition disabled:opacity-50 dark:bg-primary-600',
                  dockEmbedded
                    ? 'px-2.5 py-1 text-xs shadow-sm shadow-primary-600/20 hover:bg-primary-700 dark:hover:bg-primary-500'
                    : 'px-3.5 py-2 text-sm shadow-md shadow-primary-600/20 hover:bg-primary-700 hover:shadow-lg hover:shadow-primary-600/25 dark:hover:bg-primary-500',
                ].join(' ')}
                aria-label="Send"
              >
                {dockEmbedded ? 'Send' : 'Send ▶'}
              </button>
            </div>
          </div>
          )}
          {preferenceSavedHint ? (
            <p
              data-testid="assistant-preference-saved-hint"
              className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
              role="status"
            >
              Preference saved — future answers will use your correction.
            </p>
          ) : null}
          {suggestedPrompts.length > 0 ? (
            <div
              data-testid="assistant-suggested-prompts"
              className="mt-2 flex flex-wrap gap-1.5"
              role="group"
              aria-label="Suggested prompts"
            >
              {suggestedPrompts.map((q) => (
                <button
                  key={q}
                  type="button"
                  disabled={isLoading || flowResolving}
                  onClick={() => void sendMessageWithText(q)}
                  className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-left text-[11px] font-medium text-gray-800 shadow-sm transition hover:border-primary-300 hover:bg-primary-50/80 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-primary-600 dark:hover:bg-primary-950/30"
                >
                  {q}
                </button>
              ))}
            </div>
          ) : null}
          {variant === 'page' ? (
            <p
              className="sr-only"
              data-testid={unifiedOrchestration ? 'assistant-stream-routing' : undefined}
              data-topic-slug={
                unifiedOrchestration ? brainStripTopicSlug || undefined : undefined
              }
              data-ttft-ms={
                unifiedOrchestration && typeof lastStreamRouting?.ttftMs === 'number'
                  ? String(Math.round(lastStreamRouting.ttftMs))
                  : undefined
              }
              data-total-ms={
                unifiedOrchestration && typeof lastStreamRouting?.totalMs === 'number'
                  ? String(Math.round(lastStreamRouting.totalMs))
                  : undefined
              }
              data-world-model-fetch-ms={
                unifiedOrchestration &&
                typeof lastStreamRouting?.worldModelFetchMs === 'number'
                  ? String(Math.round(lastStreamRouting.worldModelFetchMs))
                  : undefined
              }
            >
              {brainStripTopicSlug
                ? `Topic: ${brainStripTopicSlug}`
                : 'topic routing'}
            </p>
          ) : (
            <p
              className="mt-2 break-words text-xs text-gray-500 dark:text-gray-400"
              data-testid={unifiedOrchestration ? 'assistant-stream-routing' : undefined}
              data-topic-slug={
                unifiedOrchestration ? brainStripTopicSlug || undefined : undefined
              }
              data-ttft-ms={
                unifiedOrchestration && typeof lastStreamRouting?.ttftMs === 'number'
                  ? String(Math.round(lastStreamRouting.ttftMs))
                  : undefined
              }
              data-total-ms={
                unifiedOrchestration && typeof lastStreamRouting?.totalMs === 'number'
                  ? String(Math.round(lastStreamRouting.totalMs))
                  : undefined
              }
              data-world-model-fetch-ms={
                unifiedOrchestration &&
                typeof lastStreamRouting?.worldModelFetchMs === 'number'
                  ? String(Math.round(lastStreamRouting.worldModelFetchMs))
                  : undefined
              }
            >
              {unifiedOrchestration ? (
                <>
                  {brainStripTopicSlug
                    ? `Topic: ${brainStripTopicSlug}`
                    : 'Topic routing'}
                  {typeof lastStreamRouting?.topicMatchConfidence === 'number'
                    ? ` (${Math.round(lastStreamRouting.topicMatchConfidence * 100)}%)`
                    : ''}
                  {typeof lastStreamRouting?.ttftMs === 'number'
                    ? ` · ${(lastStreamRouting.ttftMs / 1000).toFixed(1)}s`
                    : typeof lastStreamRouting?.totalMs === 'number'
                      ? ` · ${(lastStreamRouting.totalMs / 1000).toFixed(1)}s`
                      : ''}
                </>
              ) : (
                <>{useStream ? 'Streaming' : 'One-shot'}</>
              )}
            </p>
          )}
        </footer>
        ) : null}
        </div>
      </aside>
      <ApprovalModal
        show={Boolean(pendingApproval)}
        toolName={pendingApproval?.toolName ?? ''}
        toolArgs={pendingApproval?.toolArgs ?? {}}
        message={pendingApproval?.message ?? ''}
        busyAction={approvalBusy}
        errorMessage={approvalError}
        onConfirm={async () => {
          if (!pendingApproval) return;
          setApprovalBusy('approve');
          setApprovalError(null);
          try {
            const data = await approveToolCall(
              pendingApproval.threadId,
              true,
              pendingApproval.toolName,
              pendingApproval.toolArgs
            );
            setPendingApproval(null);
            const msg =
              (data?.message || '').trim() ||
              (data?.success !== false ? 'Action confirmed.' : 'Action could not be completed.');
            appendAssistantNotice(msg);
          } catch (err) {
            setApprovalError(err instanceof Error ? err.message : 'Request failed');
          } finally {
            setApprovalBusy(null);
          }
        }}
        onCancel={async () => {
          if (!pendingApproval) return;
          setApprovalBusy('deny');
          setApprovalError(null);
          try {
            const data = await approveToolCall(
              pendingApproval.threadId,
              false,
              pendingApproval.toolName,
              pendingApproval.toolArgs
            );
            setPendingApproval(null);
            const msg = (data?.message || '').trim() || 'Action cancelled by user.';
            appendAssistantNotice(msg);
          } catch (err) {
            setApprovalError(err instanceof Error ? err.message : 'Request failed');
          } finally {
            setApprovalBusy(null);
          }
        }}
      />
    </>
  );

  if (embedPortalRoot && isDrawer) {
    if (!isOpen) return null;
    return createPortal(
      <div className="relative flex min-h-0 w-full flex-col">{aiPanelMarkup}</div>,
      embedPortalRoot
    );
  }

  return aiPanelMarkup;
}
